import {
  Bell,
  BookOpen,
  Brain,
  BriefcaseBusiness,
  Calendar,
  Clock,
  CloudSun,
  Code,
  FileSearch,
  FileText,
  Globe,
  Headphones,
  Heart,
  HelpCircle,
  Mail,
  Megaphone,
  MessageSquare,
  Music,
  Share2,
  Shield,
  Smartphone,
  TrendingUp,
  Users,
  Video,
  Zap,
} from "lucide-react"
import type { ComponentType } from "react"

interface I18nText {
  ja: string
  en: string
  zh: string
}

export interface UseCaseStep {
  title: I18nText
  description: I18nText
}

export type UseCaseCategory =
  | "getting-started"
  | "productivity"
  | "sales"
  | "marketing"
  | "management"
  | "automation"
  | "creative"

export const categoryLabels: Record<UseCaseCategory, I18nText> = {
  "getting-started": { ja: "はじめの一歩", en: "Getting Started", zh: "入门" },
  productivity: { ja: "生産性", en: "Productivity", zh: "生产力" },
  sales: { ja: "営業", en: "Sales", zh: "销售" },
  marketing: { ja: "マーケティング", en: "Marketing", zh: "营销" },
  management: { ja: "マネジメント", en: "Management", zh: "管理" },
  automation: { ja: "自動化", en: "Automation", zh: "自动化" },
  creative: { ja: "クリエイティブ", en: "Creative", zh: "创意" },
}

export interface UseCaseItem {
  slug: string
  icon: ComponentType<{ className?: string }>
  title: I18nText
  description: I18nText
  category: UseCaseCategory
  steps: UseCaseStep[]
  tools: string[]
}

export const useCases: UseCaseItem[] = [
  {
    slug: "dont-know-what-to-do",
    icon: HelpCircle,
    title: {
      ja: "何をすればいいかわからない、でも大丈夫",
      en: "Don't know what to do? That's OK.",
      zh: "不知道该做什么？没关系。",
    },
    description: {
      ja: "「何がわからないかもわからない」——そんな時こそ、あなたのパートナーSophyの出番。状況を伝えるだけで、一緒に整理してくれます。",
      en: "\"I don't even know what I don't know\" — that's exactly when Sophy shines. Just explain your situation, and Sophy helps you sort it out.",
      zh: '"连自己不知道什么都不知道"——这正是Sophy发挥作用的时候。只需说明情况，Sophy就会帮你整理。',
    },
    category: "getting-started",
    steps: [
      {
        title: {
          ja: "「何したらいいかわからない」と送る",
          en: "Send 'I don't know what to do'",
          zh: "发送'我不知道该怎么办'",
        },
        description: {
          ja: "難しいことは何も必要ありません。Sophyのチャットを開いて「今、何をすればいいかわからない」と送るだけ。それだけでOKです。\n\nうまく説明できなくても心配いりません。「なんかモヤモヤする」「仕事が進まない」「頭がパンクしそう」——どんな言葉でも大丈夫。完璧な質問じゃなくていいんです。",
          en: "No complicated setup needed. Just open Sophy's chat and send 'I don't know what to do right now.' That's all it takes.\n\nDon't worry if you can't explain well. 'I feel stuck,' 'Work isn't moving,' 'My head is about to explode' — any words will do. You don't need a perfect question.",
          zh: "不需要复杂的操作。打开Sophy的聊天，发送'我不知道现在该做什么'就够了。\n\n说不清楚也没关系。'感觉卡住了''工作进展不了''脑子要炸了'——任何表达都可以。",
        },
      },
      {
        title: {
          ja: "Sophyが「どうしたの？」と聞いてくれる",
          en: "Sophy asks 'What's going on?'",
          zh: "Sophy会问'怎么了？'",
        },
        description: {
          ja: "Sophyは「どうしたの？整理しよっか」と返してくれます。そこから、あなたの状況をひとつずつ聞き出してくれます。\n\n「今どんな仕事を抱えてる？」「何が一番気になってる？」「いつまでにやらなきゃいけないことある？」——Sophyが質問してくれるので、あなたはそれに答えるだけ。頭の中のゴチャゴチャが、会話するうちに少しずつ整理されていきます。\n\n上司に相談するのはちょっと気が引ける、同僚も忙しそう——そんな時でも、Sophyはいつでも気軽に頼れるパートナーです。",
          en: "Sophy responds with 'What's going on? Want to sort things out?' Then it asks about your situation one thing at a time.\n\n'What work do you have right now?' 'What's bothering you most?' 'Any deadlines coming up?' — Sophy asks the questions, you just answer. The mess in your head gradually gets organized through conversation.\n\nWhen you hesitate to ask your boss, when colleagues seem too busy — Sophy is always there as a partner you can rely on.",
          zh: "Sophy会回复'怎么了？要一起整理一下吗？'然后逐一了解你的情况。\n\n'你现在有什么工作？''最在意什么？''有什么截止日期吗？'——Sophy来提问，你只需回答。",
        },
      },
      {
        title: {
          ja: "「まずこれからやってみよう」が見える",
          en: "'Let's start with this' becomes clear",
          zh: "'先从这个开始'变得清晰",
        },
        description: {
          ja: "話しているうちに、Sophyが「今の状況をまとめるとこうだね」と整理してくれます。そして「まずはこれからやってみない？」と、具体的な次の一歩を提案してくれます。\n\nLinearを連携していれば、そのままタスクとして登録することもできます。「やることリスト」がその場でできあがるので、あとはひとつずつ片付けるだけ。\n\n「何をすればいいかわからない」が「これをやればいい」に変わる。それがSophyと一緒に働くということです。優秀なマネージャーやメンターが隣にいるのと同じ安心感を、いつでもどこでも。",
          en: "As you talk, Sophy summarizes: 'So here's your situation.' Then suggests: 'Why don't we start with this?' — offering a concrete next step.\n\nIf you've connected Linear, it can register tasks right there. Your to-do list forms on the spot, and all you need to do is tackle them one by one.\n\n'I don't know what to do' becomes 'I know what to do.' That's what it means to work with Sophy. The same peace of mind as having a great manager or mentor beside you — anytime, anywhere.",
          zh: "聊着聊着，Sophy会总结：'你现在的情况是这样的。'然后建议：'要不先从这个开始？'——给出具体的下一步。\n\n如果连接了Linear，可以直接注册为任务。待办清单当场形成。",
        },
      },
    ],
    tools: ["Sophy"],
  },
  {
    slug: "morning-brief",
    icon: Bell,
    title: {
      ja: "朝のブリーフを自動配信",
      en: "Automated Morning Brief",
      zh: "自动发送早间简报",
    },
    description: {
      ja: "Slackのメンション、Linearのタスク状況、Gmailの重要メールを毎朝まとめて配信。1日の始まりをスムーズに。",
      en: "Get a daily digest of Slack mentions, Linear tasks, and important Gmail messages every morning.",
      zh: "每天早上汇总Slack提及、Linear任务状态和Gmail重要邮件。",
    },
    category: "productivity",
    steps: [
      {
        title: { ja: "時間を決める", en: "Set your time", zh: "设定时间" },
        description: {
          ja: "Sophyに「毎朝8時にブリーフを送って」と送るだけで設定完了です。\n\nどの情報を含めるかも自由にカスタマイズできます。「Slackのメンションだけでいい」「Linearのタスクも入れて」など、あなたの好みに合わせて調整できます。設定画面からワンクリックで変更もできるので、まずは気軽に始めてみてください。",
          en: "Just tell Sophy 'Send me a brief at 8am every morning.' You can also set it from the scheduler screen.",
          zh: "只需告诉Sophy'每天早上8点发送简报'。也可以从调度器界面设置。",
        },
      },
      {
        title: {
          ja: "Sophyが情報を集める",
          en: "Sophy gathers info",
          zh: "Sophy收集信息",
        },
        description: {
          ja: "設定した時刻になると、Sophyがあなたの代わりにSlack・Linear・Gmailを自動でチェックします。\n\n未読のメンション、今日やるべきタスク、返信が必要なメール——あちこちのツールを開いて確認する手間はもうありません。Sophyがすべて見に行って、大事なものだけをピックアップしてくれます。",
          en: "At your set time, Sophy automatically checks Slack mentions, today's Linear tasks, and important Gmail messages.",
          zh: "在设定的时间，Sophy自动检查Slack提及、今天的Linear任务和重要的Gmail邮件。",
        },
      },
      {
        title: { ja: "ブリーフが届く", en: "Brief delivered", zh: "简报送达" },
        description: {
          ja: "集めた情報をひとつの見やすいまとめにして、Slack・LINE・アプリ内のお好きな場所に届けます。\n\nコーヒーを飲みながら、今日やるべきこと・返信が必要なもの・チームの動きを一目で把握。朝の10分で1日の全体像がつかめるので、「何から手をつけよう」と迷う時間がなくなります。",
          en: "Everything compiled into one summary, delivered to Slack, LINE, or in-app — wherever you prefer. See your whole day over coffee.",
          zh: "所有信息汇总成一份摘要，推送到Slack、LINE或应用内。",
        },
      },
    ],
    tools: ["Slack", "Linear", "Gmail"],
  },
  {
    slug: "slack-monitoring",
    icon: MessageSquare,
    title: {
      ja: "Slackの重要メッセージを見逃さない",
      en: "Never Miss Important Slack Messages",
      zh: "不错过Slack重要消息",
    },
    description: {
      ja: "キーワードやメンションを定期的にチェックして、SlackやLINEに通知。情報の見落としをゼロに。",
      en: "Periodically monitors keywords and mentions, notifying you via Slack or LINE.",
      zh: "定期检查关键词和提及，通过Slack或LINE通知你。",
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "気になるキーワードを伝える",
          en: "Tell your keywords",
          zh: "告诉关键词",
        },
        description: {
          ja: "「プロダクト名がSlackで出たら教えて」「自分へのメンションを30分ごとにチェックして」とSophyに伝えるだけで設定完了です。\n\n複数のキーワードを登録したり、特定のチャンネルだけを対象にすることもできます。「競合名が出たときだけ通知して」のように、あなたにとって本当に大事な情報だけをキャッチする仕組みを作れます。",
          en: "'Let me know when our product name or competitor is mentioned in Slack.' 'Check my mentions every 30 minutes.'",
          zh: "'Slack中出现产品名或竞争对手名时通知我。''每30分钟检查我的提及。'",
        },
      },
      {
        title: { ja: "Sophyが見張る", en: "Sophy watches", zh: "Sophy监视" },
        description: {
          ja: "Sophyが設定した間隔でSlackをチェックし続けます。あなたが会議中でも、ランチ中でも、移動中でも関係ありません。\n\nチーム内の重要な議論や、あなたに関係のある話題を見落とす心配はもうありません。Sophyがあなたの代わりにずっと見守っています。",
          en: "Sophy periodically checks Slack. Whether you're in a meeting or at lunch, important conversations won't be missed.",
          zh: "Sophy定期检查Slack。无论你在开会还是在吃饭，都不会错过重要对话。",
        },
      },
      {
        title: {
          ja: "見つかったらすぐ通知",
          en: "Instant notification",
          zh: "立即通知",
        },
        description: {
          ja: "該当するメッセージが見つかったら、SlackのDMやLINEですぐに通知が届きます。元のメッセージへのリンクも一緒に届くので、すぐに会話に参加できます。\n\n「あの話題、気づいたときにはもう終わってた」ということがなくなります。いつでも大事なタイミングで情報をキャッチできる安心感があります。",
          en: "When a match is found, you get notified via Slack DM or LINE. No more 'I missed that discussion.'",
          zh: "找到匹配消息后，通过Slack DM或LINE通知你。",
        },
      },
    ],
    tools: ["Slack", "LINE"],
  },
  {
    slug: "linear-task-management",
    icon: Clock,
    title: {
      ja: "Linearタスクを会話で管理",
      en: "Manage Linear Tasks Through Chat",
      zh: "通过对话管理Linear任务",
    },
    description: {
      ja: "「今日やるべきタスクは？」「このイシュー完了にして」— 会話するだけでタスク管理。期限切れも自動通知。",
      en: '"What should I work on today?" "Mark this issue done" — manage tasks just by chatting.',
      zh: '"今天应该做什么？""把这个问题标记为完成"——只需对话即可管理任务。',
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "聞くだけでタスク確認",
          en: "Ask to check tasks",
          zh: "问就能查任务",
        },
        description: {
          ja: "「今日の優先タスクは？」「今週の期限切れある？」とSophyに聞くだけで、Linearを開かなくてもタスク状況がすぐにわかります。\n\nプロジェクト別、担当者別、ステータス別など、聞き方は自由自在です。朝の最初の一声で今日やるべきことが整理されるので、迷わず仕事を始められます。",
          en: "'What are my priority tasks today?' 'Any overdue this week?' — just ask Sophy instead of opening Linear.",
          zh: "'今天的优先任务是什么？''这周有过期的吗？'——问Sophy就行，不用打开Linear。",
        },
      },
      {
        title: {
          ja: "会話でタスクを操作",
          en: "Manage via chat",
          zh: "通过对话操作",
        },
        description: {
          ja: "「これ完了にして」「新しいタスク作って、来週金曜まで」と送るだけで、タスクの作成・更新・ステータス変更ができます。\n\nLinearの画面を開いてフォームに入力する手間はもうありません。会話の流れの中でそのままタスクを操作できるので、思いついたことをすぐに形にできます。",
          en: "'Mark this done.' 'Create a new task, due next Friday.' — create and update issues just by talking.",
          zh: "'标记完成。''创建新任务，下周五到期。'——对话即可创建和更新。",
        },
      },
      {
        title: {
          ja: "期限切れは自動でお知らせ",
          en: "Overdue auto-alerts",
          zh: "逾期自动提醒",
        },
        description: {
          ja: "「毎朝、期限切れのタスクがあったら教えて」と設定しておけば、タスクが溜まる前にSophyがお知らせしてくれます。\n\nあなたが忙しくてLinearを見る余裕がなくても、Sophyが忘れずにチェックしてくれます。大事なタスクが放置されることがなくなるので、チーム全体の進捗管理にも役立ちます。",
          en: "Set up overdue checks in the scheduler, and Sophy will remind you before tasks pile up.",
          zh: "在调度器中设置逾期检查，Sophy会在任务堆积前提醒你。",
        },
      },
    ],
    tools: ["Linear"],
  },
  {
    slug: "email-summary",
    icon: Mail,
    title: {
      ja: "メールを要約して重要なものだけ教えてくれる",
      en: "Email Summaries — Only What Matters",
      zh: "邮件摘要——只告诉你重要的",
    },
    description: {
      ja: "大量のメールから重要なものだけを抽出・要約。返信の下書きも作成してくれるので、メール処理が一瞬に。",
      en: "Extracts and summarizes only important emails. Even drafts replies — email processing in seconds.",
      zh: "从大量邮件中提取并总结重要内容。还能帮你起草回复。",
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "メールを連携する",
          en: "Connect your email",
          zh: "连接邮箱",
        },
        description: {
          ja: "設定画面からGmailを連携します。ワンクリックで接続できるので、難しい設定は一切ありません。\n\n連携が完了すれば、Sophyがあなたの受信トレイにアクセスできるようになります。メールの内容を外部に送ることはないので、安心して使ってください。",
          en: "Connect Gmail from the settings page. One click to connect.",
          zh: "从设置页面连接Gmail。一键连接。",
        },
      },
      {
        title: {
          ja: "「今朝のメール教えて」",
          en: "'Show me today's emails'",
          zh: "'告诉我今早的邮件'",
        },
        description: {
          ja: "「今朝のメール教えて」と聞くだけで、Sophyが受信トレイをチェックし、重要なものだけをピックアップしてくれます。\n\n内容を要約した上で「これは今日中に返信が必要」「これは後で読めばOK」と分類。大量のメールを1通ずつ開いて確認する必要はもうありません。",
          en: "Sophy checks your inbox, picks up only what matters. Summarizes and categorizes: 'needs reply' vs 'read later.'",
          zh: "Sophy检查收件箱，只挑出重要的。总结并分类：'需要回复'和'稍后阅读'。",
        },
      },
      {
        title: {
          ja: "返信もお任せ",
          en: "Replies handled too",
          zh: "回复也交给它",
        },
        description: {
          ja: "「この返信書いて」と頼めば、Sophyがメールの文脈を理解した上で返信の下書きを作成してくれます。\n\nあなたは内容を確認して、必要があれば少し手を加えて送るだけ。1通あたり数分かかっていた返信作業が、数秒で終わるようになります。",
          en: "'Draft a reply to this' — Sophy creates a context-aware draft. You just review and send.",
          zh: "'帮我回复这封'——Sophy会创建有上下文感知的草稿。你只需确认发送。",
        },
      },
    ],
    tools: ["Gmail", "メール"],
  },
  {
    slug: "document-search",
    icon: FileSearch,
    title: {
      ja: "Notion・Google Driveから自然言語で検索",
      en: "Search Notion & Google Drive in Natural Language",
      zh: "用自然语言搜索Notion和Google Drive",
    },
    description: {
      ja: "「先月の会議の議事録どこ？」「デザインガイドラインのドキュメント見せて」— 聞くだけで見つかる。",
      en: '"Where are last month\'s meeting notes?" "Show me the design guidelines" — just ask and find.',
      zh: '"上个月的会议纪要在哪？""给我看设计指南文档"——只需问就能找到。',
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "ツールを連携する",
          en: "Connect your tools",
          zh: "连接工具",
        },
        description: {
          ja: "設定画面からNotionとGoogle Driveを連携します。どちらか片方だけでも、両方でもOKです。\n\n連携すれば、Sophyがあなたのドキュメントを横断的に探せるようになります。「あのファイルどこだっけ」と自分で探し回る必要はもうありません。",
          en: "Connect Notion and Google Drive from settings. Either one alone works too.",
          zh: "从设置连接Notion和Google Drive。只连一个也行。",
        },
      },
      {
        title: {
          ja: "普通の言葉で聞く",
          en: "Ask in plain language",
          zh: "用普通话问",
        },
        description: {
          ja: "「先月の会議メモどこ？」「マーケティング予算のファイル見せて」と、普段使っている言葉でそのまま聞いてください。\n\nファイル名やフォルダの場所を正確に覚えていなくても大丈夫です。Sophyが内容から推測して、関連するドキュメントを見つけてくれます。",
          en: "'Where are last month's meeting notes?' 'Show me the marketing budget file' — no need to remember file names.",
          zh: "'上个月的会议记录在哪？''给我看营销预算文件'——不需要记住文件名。",
        },
      },
      {
        title: {
          ja: "見つけて要約してくれる",
          en: "Found and summarized",
          zh: "找到并总结",
        },
        description: {
          ja: "NotionとGoogle Driveの両方から横断的に検索して、関連するドキュメントを見つけてくれます。\n\n見つかったドキュメントの内容もその場で要約してくれるので、いちいちファイルを開かなくても中身がわかります。「これじゃなかった」と何度も開き直す手間がなくなります。",
          en: "Searches across both Notion and Google Drive, finds related documents. Summarizes content so you know what's inside before opening.",
          zh: "跨Notion和Google Drive搜索，找到相关文档。还会总结内容。",
        },
      },
    ],
    tools: ["Notion", "Google Drive"],
  },
  {
    slug: "scheduled-checks",
    icon: Calendar,
    title: {
      ja: "定期チェック＆リマインダー",
      en: "Scheduled Checks & Reminders",
      zh: "定期检查和提醒",
    },
    description: {
      ja: "「毎週金曜にLinearの未完了タスクを教えて」「毎朝9時にSlackのまとめを送って」— 好きなタイミングで自動実行。",
      en: '"Show me incomplete tasks every Friday" "Send Slack summary at 9am daily" — automated on your schedule.',
      zh: '"每周五告诉我未完成的任务""每天早上9点发送Slack摘要"——按你的时间表执行。',
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "やってほしいことを伝える",
          en: "Tell what you need",
          zh: "告诉需要什么",
        },
        description: {
          ja: "「毎週金曜にLinearの未完了タスクを教えて」「毎朝9時にSlackのまとめを送って」と、やりたいことをそのまま伝えるだけで設定完了です。\n\n複雑な設定画面を操作する必要はありません。Sophyが内容を理解して、自動実行のスケジュールを組んでくれます。もちろん、あとから頻度や内容を変更するのも会話ひとつで対応できます。",
          en: "'Show me incomplete Linear tasks every Friday.' 'Send a Slack summary at 9am daily.' Just say what you want.",
          zh: "'每周五告诉我Linear未完成的任务。''每天早上9点发送Slack摘要。'",
        },
      },
      {
        title: {
          ja: "Sophyが決まった時間に実行",
          en: "Sophy runs on schedule",
          zh: "Sophy按时执行",
        },
        description: {
          ja: "1回だけ、毎日、毎週、毎月——好きな頻度で自動実行できます。あなたが忘れても、Sophyは絶対に忘れません。\n\n「金曜の夕方にやろうと思ってたけど忙しくて...」ということがなくなります。決めたタイミングで確実に実行されるので、ルーティンワークから解放されます。",
          en: "Once, daily, weekly, monthly — runs at whatever frequency you choose. You might forget, but Sophy won't.",
          zh: "一次、每天、每周、每月——按你选择的频率执行。你可能会忘，但Sophy不会。",
        },
      },
      {
        title: {
          ja: "結果がチャットに届く",
          en: "Results in your chat",
          zh: "结果发送到聊天",
        },
        description: {
          ja: "チェック結果やリマインダーが、Slack・LINE・アプリ内チャットなど、お好きな場所に届きます。\n\n通知を受け取る場所は自由に選べます。仕事中はSlack、移動中はLINEなど、あなたのライフスタイルに合わせて設定できます。結果を見てすぐにSophyに追加の指示を出すこともできます。",
          en: "Check results and reminders arrive in Slack, LINE, or in-app chat. You choose where.",
          zh: "检查结果和提醒会发送到Slack、LINE或应用内聊天。你选择发送位置。",
        },
      },
    ],
    tools: ["Linear", "Slack", "Gmail"],
  },
  {
    slug: "personalized-ai",
    icon: Brain,
    title: {
      ja: "パーソナライズされた壁打ち相手",
      en: "Your Personalized Thinking Partner",
      zh: "你的个性化思考伙伴",
    },
    description: {
      ja: "あなたの性格、仕事のスタイル、過去の会話を覚えているAI。使えば使うほど、あなたに合ったアドバイスができるように。",
      en: "An AI that remembers your personality, work style, and past conversations. The more you use it, the better it understands you.",
      zh: "记住你的性格、工作风格和过去对话的AI。使用越多，越能给出适合你的建议。",
    },
    category: "getting-started",
    steps: [
      {
        title: {
          ja: "普段通り使うだけ",
          en: "Just use it normally",
          zh: "正常使用即可",
        },
        description: {
          ja: "特別な設定は不要です。Sophyと会話するだけで、あなたの好み・仕事のスタイル・大事にしていることを自然に学んでいきます。\n\nたとえば「結論から先に言ってほしい」「箇条書きが好き」といった好みも、使ううちにSophyが理解していきます。最初から完璧である必要はなく、一緒に使いながら育てていく感覚です。",
          en: "No special setup needed. Just by chatting, Sophy naturally learns your preferences, work style, and priorities.",
          zh: "无需特殊设置。只需聊天，Sophy就会自然学习你的偏好、工作风格和优先事项。",
        },
      },
      {
        title: {
          ja: "会話が「あなた仕様」になる",
          en: "Conversations become yours",
          zh: "对话变成'你的专属'",
        },
        description: {
          ja: "過去の会話を覚えているから、同じことを何度も説明しなくて大丈夫です。あなたの文脈を理解した上でアドバイスしてくれます。\n\n「先週話したあの件、どうなった？」と聞けば、Sophyは何の話か理解しています。毎回ゼロから説明し直すストレスがなくなり、会話がどんどんスムーズになっていきます。",
          en: "Sophy remembers past conversations, so you don't repeat yourself. Advice comes with your context already understood.",
          zh: "Sophy记住过去的对话，不需要重复说明。建议已经理解了你的上下文。",
        },
      },
      {
        title: {
          ja: "考えの整理・壁打ちに",
          en: "Organize thoughts & brainstorm",
          zh: "整理思路和头脑风暴",
        },
        description: {
          ja: "「これどう思う？」「AとBどっちがいいかな」と気軽に相談できます。あなたのことを知っているからこそ、的確な壁打ち相手になれます。\n\nあなたの判断基準や過去の意思決定パターンを踏まえた上で意見をくれるので、一般的なAIとは違う深さがあります。考えを整理したいとき、いつでも頼れる存在です。",
          en: "'What do you think about this?' 'A or B?' — because Sophy knows you, it gives relevant input.",
          zh: "'你觉得这个怎么样？''A和B哪个好？'——因为Sophy了解你，能给出有针对性的意见。",
        },
      },
    ],
    tools: ["Sophy"],
  },
  {
    slug: "one-on-one-prep",
    icon: Users,
    title: {
      ja: "1on1の準備を自動化",
      en: "Automate 1-on-1 Preparation",
      zh: "自动化1对1准备",
    },
    description: {
      ja: "メンバーの直近のタスク状況、Slackでの発言傾向をAIが自動要約。1on1の準備時間を大幅に短縮。",
      en: "AI auto-summarizes member's recent tasks and Slack activity. Dramatically reduce 1-on-1 prep time.",
      zh: "AI自动总结成员最近的任务状态和Slack发言趋势。大幅缩短1对1准备时间。",
    },
    category: "management",
    steps: [
      {
        title: {
          ja: "メンバーの名前を伝える",
          en: "Name the team member",
          zh: "说出成员名字",
        },
        description: {
          ja: "「田中さんの最近の状況を教えて」と、1on1の前にSophyに聞くだけで準備が始まります。\n\n複数のメンバーがいる場合も、名前を伝えるだけでそれぞれの状況をまとめてくれます。Linearを開いてフィルタリングしたり、Slackを遡って確認する手間はもうありません。",
          en: "'Tell me about Tanaka's recent situation' — just ask Sophy before the 1-on-1.",
          zh: "'告诉我田中最近的情况'——1对1之前问Sophy就行。",
        },
      },
      {
        title: {
          ja: "Sophyがツールを横断チェック",
          en: "Sophy cross-checks tools",
          zh: "Sophy跨工具检查",
        },
        description: {
          ja: "Sophyがあなたの代わりに、Linearのタスク進捗、Slackでの活動量、最近のやりとりをまとめてチェックします。\n\n複数のツールを横断して情報を集めるので、手作業で1つずつ確認する必要はありません。メンバーの活動の全体像を、数秒で把握できます。",
          en: "Linear task progress, Slack activity, recent interactions — all checked automatically. No manual research needed.",
          zh: "Linear任务进度、Slack活动量、最近的互动——全部自动检查。无需手动调查。",
        },
      },
      {
        title: {
          ja: "準備完了、5分で",
          en: "Prep done in 5 minutes",
          zh: "5分钟准备完成",
        },
        description: {
          ja: "「今週完了したタスク」「進行中の課題」「気になるポイント」がまとまったブリーフを受け取れます。\n\nこれまで30分かけていた1on1の準備が、わずか5分で完了します。空いた時間でメンバーとの対話そのものに集中できるので、1on1の質も上がります。",
          en: "Get a brief with 'tasks completed this week,' 'ongoing challenges,' and 'points to note.' 30 minutes of prep becomes 5.",
          zh: "收到包含'本周完成的任务'、'进行中的课题'和'需注意的点'的简报。30分钟的准备变为5分钟。",
        },
      },
    ],
    tools: ["Linear", "Slack"],
  },
  {
    slug: "sales-follow-up",
    icon: BriefcaseBusiness,
    title: {
      ja: "営業フォローを自動化する",
      en: "Automate Sales Follow-ups",
      zh: "自动化销售跟进",
    },
    description: {
      ja: "商談後のフォローメール、Slackでの社内共有、Linearでのタスク作成を会話ひとつで。",
      en: "Follow-up emails, internal Slack updates, and Linear task creation after meetings — all in one conversation.",
      zh: "商谈后的跟进邮件、Slack内部共享、Linear任务创建，一次对话搞定。",
    },
    category: "sales",
    steps: [
      {
        title: {
          ja: "商談メモをSophyに共有",
          en: "Share meeting notes",
          zh: "分享商谈记录",
        },
        description: {
          ja: "商談が終わったら「今の商談メモをまとめて」とSophyに送るだけ。会話の中から要点を自動で抽出してくれます。\n\n先方の要望、次のアクション、決まったこと・決まらなかったことを整理してくれるので、記憶が新鮮なうちにメモを残せます。商談直後の慌ただしい時間でも安心です。",
          en: "After a meeting, just say 'Summarize the meeting notes.' Key points are extracted automatically.",
          zh: "商谈结束后，只需说'总结会议记录'。自动提取要点。",
        },
      },
      {
        title: {
          ja: "フォローメールと社内共有を一気に",
          en: "Email & share at once",
          zh: "一次完成邮件和内部共享",
        },
        description: {
          ja: "「フォローメールも書いて、Slackにも共有して、タスクも作って」と頼めば、すべてを一括で実行してくれます。\n\nフォローメールの下書き作成、Slackのセールスチャンネルへの要約投稿、Linearへのタスク作成——これまでバラバラにやっていた作業がひとつの会話で完結します。",
          en: "Draft follow-up email, post summary to sales Slack channel, create Linear tasks — all executed at once.",
          zh: "起草跟进邮件、发布到Slack销售频道、创建Linear任务——一次完成。",
        },
      },
      {
        title: {
          ja: "フォローリマインダーも自動",
          en: "Auto follow-up reminders",
          zh: "自动跟进提醒",
        },
        description: {
          ja: "「3日後にフォローリマインダーを送って」と頼んでおけば、Sophyが忘れずにリマインドしてくれます。\n\nフォローのタイミングを逃して商機を失うことがなくなります。あなたは次の商談に集中するだけ。フォローの管理はSophyに任せて、営業活動そのものに力を注げる環境が整います。",
          en: "'Send a follow-up reminder in 3 days' — Sophy remembers so you can focus on selling.",
          zh: "'3天后发送跟进提醒'——Sophy会记住，让你专注于商谈。",
        },
      },
    ],
    tools: ["Gmail", "Slack", "Linear"],
  },
  {
    slug: "competitor-monitoring",
    icon: TrendingUp,
    title: {
      ja: "競合・市場情報を毎日キャッチアップ",
      en: "Daily Competitor & Market Intelligence",
      zh: "每日竞争对手和市场情报",
    },
    description: {
      ja: "競合のニュースや業界動向をAIが毎日チェックしてレポート。意思決定に必要な情報を逃さない。",
      en: "AI monitors competitor news and industry trends daily and delivers reports.",
      zh: "AI每天检查竞争对手新闻和行业动态并报告。",
    },
    category: "marketing",
    steps: [
      {
        title: {
          ja: "ウォッチしたいテーマを設定",
          en: "Set topics to watch",
          zh: "设置关注主题",
        },
        description: {
          ja: "「競合A社とB社のニュースを毎朝チェックして」「AI業界のトレンドを追って」と、Sophyに送るだけで設定完了です。\n\n企業名、業界キーワード、気になるトピックなど自由に指定できます。複数テーマを組み合わせてもOK。あなたの関心に合わせて、Sophyが毎日ウォッチし続けてくれます。",
          en: "'Check news about Competitor A and B every morning.' 'Track AI industry trends.' Just tell your topics.",
          zh: "'每天早上检查竞争对手A和B的新闻。''追踪AI行业趋势。'",
        },
      },
      {
        title: {
          ja: "毎朝AIがWebを検索",
          en: "AI searches the web daily",
          zh: "AI每天搜索网络",
        },
        description: {
          ja: "設定した時刻になると、Sophyがあなたの代わりにWeb上のニュースやプレスリリースを自動で検索します。\n\n毎日手動でニュースサイトを巡回していた時間がゼロに。Sophyは指定されたキーワードに関する最新情報を集めて、重要度を判断してくれます。「今日は特に重要なニュースはないよ」と教えてくれる日もあれば、「これは要注目」とハイライトしてくれることも。",
          en: "Sophy automatically searches the latest news and press releases online. Replaces the manual daily check entirely.",
          zh: "Sophy自动搜索网上最新新闻和新闻稿。完全替代每天手动检查。",
        },
      },
      {
        title: {
          ja: "要約レポートが届く",
          en: "Summary report delivered",
          zh: "摘要报告送达",
        },
        description: {
          ja: "集めた情報の中から、重要なニュースだけをピックアップして要約レポートにまとめ、SlackやLINEに届けてくれます。\n\nそのまま朝会で共有できるクオリティなので、チームの情報格差もなくなります。「この記事の詳細を教えて」と聞けば、さらに掘り下げた解説もしてくれます。",
          en: "Important news picked up and delivered as a summary report to Slack or LINE. Ready for your team's morning standup.",
          zh: "挑选重要新闻，作为摘要报告推送到Slack或LINE。可用于团队晨会。",
        },
      },
    ],
    tools: ["Slack", "LINE"],
  },
  {
    slug: "sns-automation",
    icon: Share2,
    title: {
      ja: "SNS投稿を自律的に運用する",
      en: "Autonomous Social Media Management",
      zh: "自主运营社交媒体",
    },
    description: {
      ja: "投稿案の作成、スケジュール配信、反応のモニタリングまで。SNS運用をAIに任せて、本業に集中。",
      en: "From drafting posts to scheduled delivery and engagement monitoring. Delegate social media to AI.",
      zh: "从起草帖子到定时发布和互动监控。将社交媒体运营委托给AI。",
    },
    category: "marketing",
    steps: [
      {
        title: {
          ja: "投稿のテーマと頻度を決める",
          en: "Set theme & frequency",
          zh: "设定主题和频率",
        },
        description: {
          ja: "「毎週月・水・金にテック関連の投稿を作って」「うちのサービスの活用事例を週1で発信して」と、Sophyに伝えるだけで運用が始まります。\n\n投稿のトーンや文字数、ハッシュタグのルールなども一緒に伝えておくと、あなた好みのスタイルで投稿案を作ってくれます。面倒な運用設計はSophyにお任せください。",
          en: "'Create tech posts every Mon/Wed/Fri.' 'Share our service use cases weekly.' Set your rhythm.",
          zh: "'每周一三五创建科技相关帖子。''每周分享我们服务的使用案例。'",
        },
      },
      {
        title: {
          ja: "Sophyが投稿案を提案",
          en: "Sophy drafts posts",
          zh: "Sophy起草帖子",
        },
        description: {
          ja: "Sophyが最新のトレンドやニュースをWebで調べて、あなたのトーンに合わせた投稿案を自動で作成します。\n\n「もうちょっとカジュアルにして」「数字を入れて」といったフィードバックにもすぐ対応。やり取りを重ねるほど、あなたらしい投稿を提案できるようになります。あなたは最終チェックするだけでOKです。",
          en: "Sophy researches trends and auto-generates posts matching your tone. You just review the content.",
          zh: "Sophy调研趋势，自动生成符合你语调的帖子。你只需确认内容。",
        },
      },
      {
        title: {
          ja: "投稿・配信・モニタリング",
          en: "Post, deliver & monitor",
          zh: "发布和监控",
        },
        description: {
          ja: "承認した投稿案は、Sophyがブラウザ操作でXやInstagramに直接投稿することもできます。\n\n投稿後の反応チェックもSophyにお任せ。「いいねが多かった投稿の傾向を教えて」と聞けば、次の投稿に活かせるヒントをもらえます。SNS運用の全工程をSophyがサポートするので、あなたは本業に集中できます。",
          en: "Browser automation can post to X or Instagram. Leave engagement monitoring to Sophy and focus on your core work.",
          zh: "浏览器自动化可以发布到X或Instagram。把互动监控交给Sophy，专注核心工作。",
        },
      },
    ],
    tools: ["Slack"],
  },
  {
    slug: "marketing-content",
    icon: Megaphone,
    title: {
      ja: "マーケティングコンテンツを量産する",
      en: "Scale Marketing Content Production",
      zh: "批量生产营销内容",
    },
    description: {
      ja: "ブログ記事の下書き、メルマガの文案、LPのコピーライティング。あなたのブランドトーンを学習したAIが作成。",
      en: "Blog drafts, newsletter copy, landing page text. Created by an AI that learns your brand tone.",
      zh: "博客草稿、邮件通讯文案、落地页文案。由学习了你品牌语调的AI创建。",
    },
    category: "marketing",
    steps: [
      {
        title: {
          ja: "ブランドの雰囲気を伝える",
          en: "Share your brand voice",
          zh: "分享品牌风格",
        },
        description: {
          ja: "「うちはカジュアルなトーンで」「ターゲットは30代のマネージャー層」と、最初に1回伝えるだけでSophyが記憶します。\n\n過去のブログ記事やメルマガのURLを共有しておけば、既存コンテンツのトーンも学習。回を重ねるごとに、あなたのブランドにぴったりの文章を書けるようになっていきます。",
          en: "'We use a casual tone.' 'Our target is managers in their 30s.' Tell Sophy once and it remembers.",
          zh: "'我们用轻松的语调。''目标是30多岁的经理。'告诉Sophy一次就记住了。",
        },
      },
      {
        title: {
          ja: "アイデア出しから下書きまで",
          en: "From ideas to drafts",
          zh: "从创意到草稿",
        },
        description: {
          ja: "「来月のブログネタを5つ出して」「このテーマで1000字の記事書いて」と頼むだけ。Sophyが過去のコンテンツも参考にしながら、構成から本文まで一気に作成します。\n\nネタ出しの段階から壁打ち相手になってくれるので、「何を書こう」と悩む時間が大幅に減ります。下書きが気に入らなければ「もっとデータを入れて」「トーンを変えて」と気軽にリクエストできます。",
          en: "'Give me 5 blog ideas for next month.' 'Write a 1000-word article on this topic.' References past content too.",
          zh: "'给我下个月5个博客创意。''写一篇1000字的文章。'还会参考过去的内容。",
        },
      },
      {
        title: {
          ja: "Notionに保存、すぐ使える",
          en: "Saved to Notion, ready to use",
          zh: "保存到Notion，随时可用",
        },
        description: {
          ja: "作成したコンテンツはNotionやGoogle Driveに自動で保存されます。チームメンバーがすぐにレビュー・編集できる状態で共有されるので、ワークフローがスムーズです。\n\n「毎週水曜にブログの下書きを1本作って、Notionに保存して」と設定しておけば、あなたが何もしなくてもコンテンツが溜まっていきます。",
          en: "Content auto-saved to Notion or Google Drive. Team members can review and edit immediately.",
          zh: "内容自动保存到Notion或Google Drive。团队成员可以立即审阅和编辑。",
        },
      },
    ],
    tools: ["Notion", "Google Drive"],
  },
  {
    slug: "daily-report",
    icon: FileText,
    title: {
      ja: "日報・週報を自動生成する",
      en: "Auto-Generate Daily & Weekly Reports",
      zh: "自动生成日报和周报",
    },
    description: {
      ja: "今日の会話内容、完了タスク、Slackの活動をもとにレポートを自動作成。報告書作成の手間をゼロに。",
      en: "Auto-create reports from today's conversations, completed tasks, and Slack activity.",
      zh: "根据今天的对话内容、完成的任务和Slack活动自动创建报告。",
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "「今日の日報作って」と言うだけ",
          en: "Just say 'make today's report'",
          zh: "只需说'生成今天的日报'",
        },
        description: {
          ja: "「今日の日報作って」と1日の終わりにSophyに送るだけ。それだけで今日の成果がまとまった日報が完成します。\n\n「毎日18時に自動で日報を作って」と設定しておけば、あなたが忘れてもSophyが毎日決まった時間にレポートを用意してくれます。もう日報のために残業する必要はありません。",
          en: "Just talk to Sophy at the end of the day. Or set it to auto-generate at 6pm daily.",
          zh: "一天结束时和Sophy说一声就行。也可以设置每天18点自动生成。",
        },
      },
      {
        title: {
          ja: "Sophyが1日の活動を集約",
          en: "Sophy aggregates your day",
          zh: "Sophy汇总一天的活动",
        },
        description: {
          ja: "SophyがLinearの完了タスク、あなたとの会話内容、Slackでの活動を横断的にまとめます。「あれ、今日何やったっけ？」と思い出す手間がなくなります。\n\n手作業だと書き忘れがちな細かいタスクも、Sophyが自動で拾い上げてくれるので漏れがありません。あなたの1日の仕事を、Sophyがしっかり見てくれています。",
          en: "Aggregates Linear completed tasks, chat conversations, and Slack activity. Nothing gets forgotten.",
          zh: "汇总Linear完成的任务、聊天内容和Slack活动。不会遗漏。",
        },
      },
      {
        title: {
          ja: "所定フォーマットで完成",
          en: "Formatted and done",
          zh: "格式化完成",
        },
        description: {
          ja: "日報のフォーマットを一度伝えておけば、毎回同じ形式で生成してくれます。「やったこと・課題・明日の予定」のような定型フォーマットも、チーム独自のテンプレートもOK。\n\nSlackの指定チャンネルに自動投稿する設定にしておけば、報告の手間がゼロになります。週報も「今週の日報をまとめて週報にして」で完成です。",
          en: "Tell Sophy your report format once, and it generates in the same format every time. Can auto-post to Slack too.",
          zh: "告诉Sophy一次日报格式，每次都按同样格式生成。还可以自动发布到Slack。",
        },
      },
    ],
    tools: ["Linear", "Slack"],
  },
  {
    slug: "multi-channel",
    icon: Smartphone,
    title: {
      ja: "Slack・LINE・Webどこからでも同じSophy",
      en: "Same Sophy from Slack, LINE, or Web",
      zh: "从Slack、LINE或Web访问同一个Sophy",
    },
    description: {
      ja: "デスクではWeb、移動中はLINE、チームではSlack。どのチャネルからでも同じ文脈で会話が続く。",
      en: "Web at your desk, LINE on the go, Slack with your team. Context carries across every channel.",
      zh: "桌前用Web，移动中用LINE，团队用Slack。上下文都能延续。",
    },
    category: "getting-started",
    steps: [
      {
        title: {
          ja: "使いたいチャネルを連携",
          en: "Connect your channels",
          zh: "连接你的渠道",
        },
        description: {
          ja: "設定画面からSlackやLINEなど、使いたいチャネルをワンクリックで連携できます。Webブラウザ版はアカウント作成時からすぐに使えます。\n\n連携はいつでも追加・変更OK。「まずはWebだけ試して、慣れてきたらLINEも追加」という使い方もおすすめです。どのチャネルを追加しても、Sophyはあなたとの会話の続きを覚えています。",
          en: "Connect Slack, LINE, etc. from settings. Web is available from account creation.",
          zh: "从设置连接Slack、LINE等。Web从创建账户就可以使用。",
        },
      },
      {
        title: {
          ja: "どこからでも同じSophy",
          en: "Same Sophy everywhere",
          zh: "到处都是同一个Sophy",
        },
        description: {
          ja: "朝はSlackで「今日のタスクは？」、移動中はLINEで「あの件どうなった？」、じっくり考えたい時はWebで。どこからメッセージを送っても、同じ記憶・同じ性格のSophyが応答します。\n\nチャネルをまたいでも会話の文脈が途切れないので、「さっきの続きだけど」とどこからでも自然に会話を再開できます。いつでも頼れる相棒が、あなたのそばにいる感覚です。",
          en: "Slack in the morning, LINE on the go — wherever you ask, the same Sophy with the same memory responds.",
          zh: "早上用Slack，移动中用LINE——无论在哪问，同一个Sophy回应。",
        },
      },
      {
        title: {
          ja: "通知も今いる場所に届く",
          en: "Notifications find you",
          zh: "通知送到你在的地方",
        },
        description: {
          ja: "自動実行の通知先を「最後に使った場所」に設定しておくと、あなたが今いるチャネルに届きます。デスクにいればWeb、外出中ならLINEに届く、という具合です。\n\nもちろん「通知は常にSlackに送って」のように固定もできます。あなたのワークスタイルに合わせて、Sophyが最適な届け方をしてくれます。",
          en: "Set scheduler notifications to 'last used channel' and they'll reach you wherever you are.",
          zh: "将调度器通知设置为'最后使用的渠道'，通知就会发到你在的地方。",
        },
      },
    ],
    tools: ["Slack", "LINE"],
  },
  {
    slug: "web-research",
    icon: Globe,
    title: {
      ja: "Web情報を定期収集してレポート",
      en: "Automated Web Research & Reports",
      zh: "定期收集网络信息并报告",
    },
    description: {
      ja: "株価、天気、ニュース、技術ブログ。指定したテーマのWeb情報をAIが定期的に収集・要約して届ける。",
      en: "Stock prices, weather, news, tech blogs. AI periodically collects and summarizes web info on your topics.",
      zh: "股价、天气、新闻、技术博客。AI定期收集并总结你选择主题的网络信息。",
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "知りたいテーマを設定",
          en: "Set your topics",
          zh: "设置你的主题",
        },
        description: {
          ja: "「毎朝8時に日経平均とドル円を教えて」「毎日AIニュースをまとめて」と、知りたいことをSophyに伝えるだけで設定完了です。\n\n株価、為替、業界ニュース、技術ブログなど、テーマは自由に組み合わせられます。「前日比で大きく動いた時だけ教えて」のような条件付きのリクエストにも対応してくれます。",
          en: "'Tell me Nikkei and USD/JPY at 8am.' 'Summarize AI news daily.' Just say what you want to know.",
          zh: "'每天早上8点告诉我日经和美元/日元。''每天总结AI新闻。'",
        },
      },
      {
        title: {
          ja: "Sophyが毎日Webで調査",
          en: "Sophy researches daily",
          zh: "Sophy每天调研",
        },
        description: {
          ja: "設定した時刻になると、Sophyが自動でWebを検索して最新の情報を集めてきます。あなたが寝ている間も、会議中も、Sophyはずっとウォッチし続けています。\n\n複数のニュースサイトやブログを横断的にチェックするので、1つ1つブックマークを開いて巡回する手間がなくなります。あなたの代わりに、毎日欠かさず情報収集してくれる存在です。",
          en: "At your set time, Sophy auto-searches the web — gathering latest prices, news, and blog posts.",
          zh: "在设定时间，Sophy自动搜索网络——收集最新价格、新闻和博客文章。",
        },
      },
      {
        title: {
          ja: "要点だけ届けてくれる",
          en: "Key points delivered",
          zh: "只递送要点",
        },
        description: {
          ja: "集めた情報を要約して、SlackやLINEに配信してくれます。長い記事を全部読む時間がなくても、要点だけサッとキャッチアップできます。\n\n気になった記事があれば「これもっと詳しく教えて」と聞けば、Sophyが内容を掘り下げて解説してくれます。情報収集から理解まで、Sophyがあなたのそばでサポートします。",
          en: "Summarizes gathered info and delivers to Slack or LINE. Catch up on key points even when you don't have time to read.",
          zh: "总结收集的信息，推送到Slack或LINE。即使没时间阅读也能了解要点。",
        },
      },
    ],
    tools: ["Slack", "LINE"],
  },
  {
    slug: "meeting-prep",
    icon: Calendar,
    title: {
      ja: "会議前の準備を5分で終わらせる",
      en: "Finish Meeting Prep in 5 Minutes",
      zh: "5分钟完成会议准备",
    },
    description: {
      ja: "Google Calendarの予定を見て、関連するNotionの議事録やLinearのタスクを自動で集約。",
      en: "Checks Google Calendar, auto-aggregates related Notion notes and Linear tasks.",
      zh: "查看Google Calendar日程，自动汇总相关的Notion会议记录和Linear任务。",
    },
    category: "management",
    steps: [
      {
        title: {
          ja: "「次の会議の準備して」",
          en: "'Prepare for my next meeting'",
          zh: "'准备下一个会议'",
        },
        description: {
          ja: "「次の会議の準備して」とSophyに一言伝えるだけ。Google Calendarから次の予定を自動で確認して、参加者や議題を把握します。\n\n「毎回会議の30分前に自動で準備して」と設定しておけば、あなたが何も言わなくてもSophyが先回りして準備を始めてくれます。",
          en: "Just one sentence to Sophy. It checks Google Calendar for your next meeting, noting participants and agenda.",
          zh: "对Sophy说一句话就行。它检查Google Calendar的下一个会议，记录参与者和议程。",
        },
      },
      {
        title: {
          ja: "関連情報を自動で集約",
          en: "Auto-aggregate related info",
          zh: "自动汇总相关信息",
        },
        description: {
          ja: "Notionから過去の議事録を検索し、Linearから関連タスクの進捗を取得。バラバラだった情報を、Sophyが1つのブリーフにまとめてくれます。\n\n「前回の会議で決まったこと」「まだ完了していないアクションアイテム」など、会議で必要になりそうな情報を先回りして集めてくれるので、準備にかける時間が劇的に減ります。",
          en: "Searches Notion for past meeting notes and pulls related task progress from Linear. Everything in one brief.",
          zh: "从Notion搜索过去的会议记录，从Linear获取相关任务进度。全部汇总为一份简报。",
        },
      },
      {
        title: {
          ja: "ブリーフを受け取って会議へ",
          en: "Get your brief, go to meeting",
          zh: "收到简报，去开会",
        },
        description: {
          ja: "必要な情報がまとまったブリーフを受け取ったら、あとは会議に臨むだけ。SlackやLINE、アプリ内チャットなど、お好きな場所でブリーフを受け取れます。\n\n「今日の3つの会議、全部まとめて準備して」のように複数の会議をまとめてリクエストすることもできます。忙しい日こそ、Sophyの力を借りてください。",
          en: "Once you get your compiled brief, you're ready. Can also set 'auto-send 30 minutes before the meeting.'",
          zh: "收到汇总的简报就准备好了。也可以设置'会议前30分钟自动发送'。",
        },
      },
    ],
    tools: ["Google Calendar", "Notion", "Linear"],
  },
  {
    slug: "custom-automation",
    icon: Zap,
    title: {
      ja: "好きな自動化を自分で組む",
      en: "Build Your Own Automations",
      zh: "构建你自己的自动化",
    },
    description: {
      ja: "カスタムプロンプト × スケジューラー × 連携ツール。組み合わせ次第で、あなただけの自動化が無限に作れる。",
      en: "Custom prompts × scheduler × integrations. Combine freely to create unlimited personal automations.",
      zh: "自定义提示 × 调度器 × 集成工具。自由组合，创造无限的个人自动化。",
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "やりたいことを言葉にする",
          en: "Describe what you want",
          zh: "描述你想要的",
        },
        description: {
          ja: "「毎週金曜に今週のチャットから学びを5つ抽出して」「毎月1日に先月の成果をまとめて」など、あなたのアイデア次第でどんな自動化でも作れます。\n\nプログラミングの知識は一切不要。やりたいことを自然な言葉でSophyに伝えるだけで、自動実行の設定が完了します。「こんなこともできる？」と気軽に相談してみてください。",
          en: "'Extract 5 learnings from this week's chats every Friday.' 'Summarize last month's results on the 1st.' Anything goes.",
          zh: "'每周五从本周聊天中提取5个心得。''每月1日总结上个月的成果。'什么都行。",
        },
      },
      {
        title: {
          ja: "Sophyがスケジュール実行",
          en: "Sophy runs on schedule",
          zh: "Sophy按计划执行",
        },
        description: {
          ja: "毎日、毎週、毎月など、好きなタイミングでSophyが自動で実行してくれます。SlackやLinear、Notionなど連携ツールのデータも組み合わせて使えます。\n\nあなたが忘れても、Sophyは忘れません。一度設定すれば、あとはSophyが決まった時間にきちんと動いてくれます。いつでも頼れるパートナーがそばにいる安心感です。",
          en: "Daily, weekly, monthly — Sophy auto-runs at your preferred timing. Uses data from your connected tools too.",
          zh: "每天、每周、每月——Sophy在你选择的时间自动运行。也使用连接工具的数据。",
        },
      },
      {
        title: { ja: "結果を受け取る", en: "Receive results", zh: "接收结果" },
        description: {
          ja: "実行結果はSlack・LINE・アプリ内チャットなど、お好きな場所で受け取れます。「食事計画を毎週作って」「チームメンバーの誕生日を祝って」「毎朝天気予報を教えて」など、アイデアは無限大。\n\nSophyはあなた専用のパートナー。仕事のことだけでなく、プライベートのちょっとした自動化にも使えます。あなたの日常をもっとスムーズにするお手伝いをします。",
          en: "Results delivered via Slack, LINE, or in-app chat. 'Create a meal plan weekly.' 'Wish birthday members.' The possibilities are endless.",
          zh: "结果通过Slack、LINE或应用内聊天接收。可能性无限。",
        },
      },
    ],
    tools: ["Slack", "Linear", "Notion", "Gmail"],
  },
  {
    slug: "ugc-video-production",
    icon: Video,
    title: {
      ja: "UGC動画をAIで量産する",
      en: "Mass-Produce UGC Videos with AI",
      zh: "用AI批量制作UGC视频",
    },
    description: {
      ja: "Seedance 2.0やKling 3.0を使って、撮影なしでUGC風の動画を自動生成。SNS広告やショート動画の制作コストを劇的に削減。",
      en: "Use Seedance 2.0 or Kling 3.0 to auto-generate UGC-style videos without filming. Dramatically cut production costs for social ads and short videos.",
      zh: "使用Seedance 2.0或Kling 3.0自动生成UGC风格视频，无需拍摄。大幅降低社交广告和短视频的制作成本。",
    },
    category: "creative",
    steps: [
      {
        title: {
          ja: "台本と素材をSophyに渡す",
          en: "Give Sophy your script & assets",
          zh: "把脚本和素材交给Sophy",
        },
        description: {
          ja: "「この商品紹介の台本で、UGC風のショート動画を作って」とSophyに送るだけ。テキストの台本、参考画像、商品写真など、手持ちの素材を渡せばOKです。\n\nSeedance 2.0はテキスト・画像・動画・音声の4つを同時に入力できるので、「こんなトーンで」「このBGMを使って」といった細かい指定も可能。Kling 3.0なら4K/60fpsのハイクオリティな映像も作れます。",
          en: "Just send Sophy 'Make a UGC-style short video with this product script.' Pass your text script, reference images, or product photos. Seedance 2.0 accepts text, images, video, and audio simultaneously, so you can specify tone and BGM. Kling 3.0 delivers up to 4K/60fps quality.",
          zh: "只需把台本、参考图片或产品照片发给Sophy。Seedance 2.0同时接受文本、图片、视频和音频输入。Kling 3.0可生成4K/60fps高质量视频。",
        },
      },
      {
        title: {
          ja: "Sophyが動画を自動生成",
          en: "Sophy auto-generates videos",
          zh: "Sophy自动生成视频",
        },
        description: {
          ja: "SophyがSeedance 2.0やKling 3.0を使って、リアルなAI人物が商品を紹介するUGC風動画を生成します。リップシンク（口の動きと音声の同期）は8言語以上に対応、表情も自然です。\n\n2026年3月にリリースされたSeedance 2.0は2K解像度のネイティブ動画を1分以内に生成。Kling 3.0はAIディレクター機能で1本の動画内に最大6カットを自動構成してくれます。撮影・キャスティング・編集の手間がまるごと不要になります。",
          en: "Sophy uses Seedance 2.0 or Kling 3.0 to generate UGC-style videos with realistic AI personas. Lip-sync supports 8+ languages with natural expressions. Seedance 2.0 generates native 2K video in under a minute. Kling 3.0's AI Director auto-composes up to 6 shots per video.",
          zh: "Sophy使用Seedance 2.0或Kling 3.0生成逼真的AI人物UGC风格视频。唇形同步支持8种以上语言。Seedance 2.0一分钟内生成原生2K视频。",
        },
      },
      {
        title: {
          ja: "量産・配信まで自動化",
          en: "Automate mass production & delivery",
          zh: "自动化批量生产和分发",
        },
        description: {
          ja: "1本作って終わりじゃありません。「毎週3本、商品別にバリエーション違いの動画を作って」と設定すれば、Sophyが自動で量産してくれます。\n\n完成した動画はGoogle Driveに自動保存。Slackで「今週の動画できたよ」と通知が届くので、確認してSNSに投稿するだけ。従来なら1本10万円以上かかっていたUGC動画が、AIなら数百円のコストで作れる時代です。マーケティングチームの動画制作を根本から変えます。",
          en: "Not just one video. Set 'Make 3 product variation videos weekly' and Sophy mass-produces them automatically. Finished videos auto-save to Google Drive with Slack notifications. What used to cost $1,000+ per UGC video now costs pennies with AI.",
          zh: "不只是一个视频。设置'每周制作3个产品变体视频'，Sophy自动批量生产。成品自动保存到Google Drive，Slack通知送达。",
        },
      },
    ],
    tools: ["Seedance", "Kling", "Google Drive"],
  },
  {
    slug: "tiktok-marketing",
    icon: Share2,
    title: {
      ja: "TikTokマーケティングをAIで回す",
      en: "Run TikTok Marketing with AI",
      zh: "用AI运营TikTok营销",
    },
    description: {
      ja: "トレンドリサーチ、台本作成、動画生成、投稿スケジュールまで。TikTok運用の8割をSophyに任せて、企画に集中。",
      en: "Trend research, scriptwriting, video generation, posting schedule. Delegate 80% of TikTok operations to Sophy.",
      zh: "趋势调研、脚本创作、视频生成、发布排期。将TikTok运营的80%交给Sophy。",
    },
    category: "creative",
    steps: [
      {
        title: {
          ja: "トレンドを調べてもらう",
          en: "Research trends",
          zh: "调研趋势",
        },
        description: {
          ja: "「TikTokで今バズってるフォーマットを教えて」「うちの業界で伸びてる動画の傾向は？」とSophyに聞くだけ。\n\nSophyがWeb検索でTikTokの最新トレンド、人気のハッシュタグ、伸びているフォーマット（ビフォーアフター、ストリートインタビュー風、ASMR等）をリサーチして報告してくれます。毎週月曜に自動でトレンドレポートを届ける設定にもできます。",
          en: "'What formats are trending on TikTok now?' 'What video styles are growing in our industry?' Just ask Sophy.\n\nSophy researches the latest TikTok trends, popular hashtags, and growing formats via web search. You can also set it to auto-deliver a weekly trend report every Monday.",
          zh: "'TikTok现在什么格式在火？''我们行业哪种视频风格在增长？'问Sophy就行。\n\nSophy通过网络搜索调研最新TikTok趋势、热门话题标签和增长中的格式。",
        },
      },
      {
        title: {
          ja: "台本と動画をAIで作る",
          en: "Create scripts & videos with AI",
          zh: "用AI创建脚本和视频",
        },
        description: {
          ja: "トレンドをもとに「この商品で15秒のTikTok用台本を3パターン作って」と依頼。Sophyがフック（冒頭の掴み）、本題、CTAの構成で台本を作成します。\n\nそのままSeedance 2.0やKling 3.0で動画化も可能。AI人物がリップシンクで商品を紹介するUGC風動画が、撮影なし・キャスティングなしで完成します。1本あたり数分で量産できるので、A/Bテスト用のバリエーション違いも簡単に作れます。",
          en: "Based on trends, ask 'Create 3 TikTok script patterns for this product in 15 seconds.' Sophy creates scripts with hook, body, and CTA structure.\n\nThen generate videos with Seedance 2.0 or Kling 3.0. UGC-style videos with AI lip-sync presenters — no filming, no casting. Each video takes minutes, making A/B test variations easy.",
          zh: "基于趋势，要求'为这个产品创建3个15秒的TikTok脚本'。Sophy用开头、正文、CTA结构创建脚本。\n\n然后用Seedance 2.0或Kling 3.0生成视频。",
        },
      },
      {
        title: {
          ja: "投稿スケジュールを自動化",
          en: "Automate posting schedule",
          zh: "自动化发布排期",
        },
        description: {
          ja: "「毎週火・木・土の19時に投稿して」とスケジュールを設定。Sophyがブラウザ自動化でTikTokに直接投稿することもできます。\n\n投稿後の反応（再生数、いいね、コメント数）もSophyがチェックして「今週一番伸びた動画はこれ」「このフォーマットが反応いい」とSlackで報告。次の企画に活かせるインサイトを自動で届けてくれます。制作会社に月50万円払っていたTikTok運用が、Sophyなら月数千円で回せます。",
          en: "Set 'Post every Tue/Thu/Sat at 7pm.' Sophy can post directly to TikTok via browser automation.\n\nSophy also checks post performance (views, likes, comments) and reports to Slack: 'This week's top video was X' and 'This format got the best engagement.' Insights for your next plan, delivered automatically.",
          zh: "设置'每周二四六晚7点发布'。Sophy可以通过浏览器自动化直接发布到TikTok。\n\nSophy还会检查发布后的表现并在Slack报告。",
        },
      },
    ],
    tools: ["TikTok", "Seedance", "Kling", "Slack"],
  },
  {
    slug: "instagram-marketing",
    icon: Share2,
    title: {
      ja: "Instagram運用をAIで効率化する",
      en: "Streamline Instagram Marketing with AI",
      zh: "用AI优化Instagram运营",
    },
    description: {
      ja: "フィード投稿、リール動画、ストーリーズの企画・制作・投稿をAIで自動化。ブランドの世界観を保ちながら、運用コストを1/10に。",
      en: "Automate feed posts, Reels, and Stories — planning, creation, and posting with AI. Maintain brand aesthetics at 1/10th the cost.",
      zh: "用AI自动化信息流帖子、Reels和Stories的策划、制作和发布。保持品牌调性的同时，运营成本降至1/10。",
    },
    category: "creative",
    steps: [
      {
        title: {
          ja: "ブランドのトーンをSophyに覚えさせる",
          en: "Teach Sophy your brand tone",
          zh: "让Sophy记住品牌调性",
        },
        description: {
          ja: "「うちのInstagramはナチュラルで温かみのあるトーン」「ターゲットは25〜35歳の女性」「カラーはベージュとアースカラー中心」——最初に1回伝えるだけで、Sophyがブランドガイドラインとして記憶します。\n\n過去の投稿をNotionやGoogle Driveに保存していれば、それも参照材料に。Sophyはあなたのブランドの世界観を理解した上で、一貫したトーンのコンテンツを提案してくれます。外注のデザイナーに毎回ブランドガイドを共有する手間がなくなります。",
          en: "'Our Instagram is natural and warm.' 'Target is women 25-35.' 'Colors are beige and earth tones.' Tell Sophy once and it remembers as brand guidelines.\n\nIf past posts are saved in Notion or Google Drive, those become reference material too. Sophy understands your brand aesthetic and proposes consistent content.",
          zh: "'我们的Instagram是自然温暖的风格。''目标是25-35岁女性。''颜色以米色和大地色为主。'告诉Sophy一次就记住了。",
        },
      },
      {
        title: {
          ja: "投稿コンテンツを一括で作る",
          en: "Batch-create content",
          zh: "批量创建内容",
        },
        description: {
          ja: "「来週のフィード投稿を5つ作って」「リール用の15秒動画を3本」「ストーリーズ用のアンケートネタを考えて」——Sophyに頼むだけで、1週間分のコンテンツが30分で完成します。\n\nキャプションはブランドトーンに合わせて自動生成。ハッシュタグも最適なものをAIが選定（リーチ率が平均40%向上するという調査結果も）。リール動画はSeedance 2.0やKling 3.0で撮影なしで制作可能。画像はAI画像生成で世界観に合ったビジュアルを作成できます。",
          en: "'Create 5 feed posts for next week.' '3 Reels videos, 15 seconds each.' 'Think up Stories poll ideas.' One request to Sophy and a week's content is done in 30 minutes.\n\nCaptions auto-generated in your brand tone. AI selects optimal hashtags. Reels created with Seedance 2.0 or Kling 3.0 — no filming needed.",
          zh: "'创建下周的5个信息流帖子。''3个15秒的Reels视频。''想一些Stories投票创意。'一个请求，一周的内容30分钟搞定。",
        },
      },
      {
        title: {
          ja: "投稿・分析・改善を自動で回す",
          en: "Automate posting, analysis & improvement",
          zh: "自动化发布、分析和改进",
        },
        description: {
          ja: "ブラウザ自動化でInstagramへの投稿を自動化。「フィードは毎日20時」「リールは水・金の12時」「ストーリーズは毎日3回」など、細かいスケジュール設定ができます。\n\n投稿後はエンゲージメント（いいね、保存、シェア、コメント）をSophyが自動チェック。「保存数が多い投稿はこのタイプ」「リーチが伸びた時間帯はこれ」といったインサイトをSlackで毎週レポート。データに基づいた改善提案まで自動で届きます。インスタ運用代行に月30〜50万円かけていた企業が、Sophyで月数千円に削減した事例も。",
          en: "Browser automation handles Instagram posting. Set 'Feed daily at 8pm,' 'Reels Wed/Fri at noon,' 'Stories 3 times daily.'\n\nAfter posting, Sophy auto-checks engagement and delivers weekly Slack reports with insights: 'Posts with this format get the most saves' and 'Best posting times for reach.' Data-driven improvement suggestions delivered automatically.",
          zh: "浏览器自动化处理Instagram发布。设置'每天晚8点发信息流''周三五中午发Reels''每天3次Stories'。\n\n发布后Sophy自动检查互动数据，每周在Slack发送洞察报告。",
        },
      },
    ],
    tools: ["Instagram", "Seedance", "Kling", "Notion", "Slack"],
  },
  {
    slug: "x-brand-monitoring",
    icon: Share2,
    title: {
      ja: "X（Twitter）でブランドの評判を監視する",
      en: "Monitor Your Brand Reputation on X",
      zh: "在X上监控品牌声誉",
    },
    description: {
      ja: "自社や競合のブランド名がXで言及されたら自動検出。ネガティブな投稿にはすぐ対応、ポジティブな声は社内共有。",
      en: "Auto-detect when your brand or competitors are mentioned on X. Respond to negative posts quickly, share positive voices internally.",
      zh: "自动检测品牌或竞争对手在X上的提及。快速回应负面帖子，内部分享正面声音。",
    },
    category: "marketing",
    steps: [
      {
        title: {
          ja: "監視キーワードを設定する",
          en: "Set monitoring keywords",
          zh: "设置监控关键词",
        },
        description: {
          ja: "「うちのブランド名と競合3社のXでの言及を毎日チェックして」とSophyに伝えるだけ。ブランド名、商品名、ハッシュタグなど、監視したいキーワードを自由に指定できます。\n\nネガティブな投稿（クレーム、不満、批判）とポジティブな投稿（称賛、推薦）を自動で分類するので、緊急対応が必要なものだけアラートを受け取ることも可能です。",
          en: "Just tell Sophy 'Check mentions of our brand and 3 competitors on X daily.' Specify brand names, product names, hashtags — any keywords you want monitored.\n\nNegative posts (complaints, criticism) and positive posts (praise, recommendations) are auto-classified, so you can set alerts for urgent items only.",
          zh: "只需告诉Sophy'每天检查我们品牌和3个竞争对手在X上的提及'。",
        },
      },
      {
        title: {
          ja: "Sophyが毎日Xをチェック",
          en: "Sophy checks X daily",
          zh: "Sophy每天检查X",
        },
        description: {
          ja: "SophyがWeb検索とブラウザ自動化を使って、Xの投稿を定期的にチェックします。あなたのブランドに言及した投稿を見つけたら、内容を要約してSlackに報告。\n\n「今日は自社名の言及が12件、うちポジティブ9件、ネガティブ2件、ニュートラル1件」のようにまとめてくれます。ネガティブ投稿はリンク付きで即座に共有されるので、炎上の火種を早期に発見できます。",
          en: "Sophy periodically checks X posts using web search and browser automation. When it finds mentions of your brand, it summarizes and reports to Slack.\n\n'Today: 12 brand mentions — 9 positive, 2 negative, 1 neutral.' Negative posts are shared with links immediately for early detection.",
          zh: "Sophy定期使用网络搜索和浏览器自动化检查X帖子。找到品牌提及后总结并报告到Slack。",
        },
      },
      {
        title: {
          ja: "レポートと対応を自動化",
          en: "Automate reports & responses",
          zh: "自动化报告和回应",
        },
        description: {
          ja: "週次で「今週のXブランドレポート」を自動生成。言及数の推移、感情分析の傾向、注目すべき投稿をまとめてくれます。\n\nポジティブな投稿は「社内で共有しよう」とSlackのチームチャネルに自動投稿。ネガティブな投稿には返信の下書きを作成して提案。対応の判断はあなたが行い、実行はSophyがサポートします。",
          en: "Auto-generate 'Weekly X Brand Report' — mention trends, sentiment analysis, notable posts.\n\nPositive posts auto-shared to team Slack. Negative posts get draft response suggestions. You decide, Sophy executes.",
          zh: "自动生成'每周X品牌报告'——提及趋势、情感分析、值得注意的帖子。",
        },
      },
    ],
    tools: ["X", "Slack"],
  },
  {
    slug: "github-pr-management",
    icon: Code,
    title: {
      ja: "GitHubのPR・Issueを会話で管理する",
      en: "Manage GitHub PRs & Issues via Chat",
      zh: "通过对话管理GitHub PR和Issue",
    },
    description: {
      ja: "レビュー待ちPRの通知、Issue状況の確認、コードレビュー依頼をSophyに任せる。開発チームの情報共有を加速。",
      en: "PR review notifications, issue status checks, code review requests — all through Sophy. Accelerate dev team communication.",
      zh: "PR审查通知、Issue状态确认、代码审查请求——全部通过Sophy。加速开发团队沟通。",
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "「レビュー待ちのPR教えて」",
          en: "'Show me PRs awaiting review'",
          zh: "'告诉我等待审查的PR'",
        },
        description: {
          ja: "Sophyに聞くだけで、あなたにアサインされたレビュー待ちPRの一覧が出てきます。タイトル、作成者、変更ファイル数、作成日時がまとめて表示されるので、GitHubを開かなくても優先順位がわかります。\n\n「今週マージされたPRは？」「このリポジトリのオープンIssueは？」など、自然な言葉で聞けます。毎朝「レビュー待ちPRがあれば教えて」と自動チェックを設定することも。",
          en: "Just ask Sophy and see all PRs assigned to you for review — title, author, files changed, creation date. No need to open GitHub to prioritize.\n\n'What PRs merged this week?' 'Open issues in this repo?' Ask naturally. Set 'notify me of pending PRs every morning' too.",
          zh: "问Sophy就能看到分配给你审查的PR列表。不用打开GitHub就能确定优先级。",
        },
      },
      {
        title: {
          ja: "Issue作成もチャットで",
          en: "Create issues via chat",
          zh: "通过聊天创建Issue",
        },
        description: {
          ja: "「ログイン画面のバグ、Issueにして」と頼むだけ。Sophyがタイトル、説明、ラベルを自動で設定してGitHubにIssueを作成してくれます。\n\n会議中に出たバグ報告や改善案を、その場でSophyに伝えてIssue化。後で「あれ何だったっけ」がなくなります。SlackでSophyに話しかけてもOKなので、開発チーム全員がどこからでもIssueを起票できます。",
          en: "'Log the login screen bug as an issue.' Sophy auto-sets title, description, and labels to create the GitHub issue.\n\nReport bugs from meetings on the spot. Works from Slack too — anyone on the team can create issues from anywhere.",
          zh: "'把登录页面的bug创建为Issue'。Sophy自动设置标题、描述和标签。",
        },
      },
      {
        title: {
          ja: "開発の進捗を自動レポート",
          en: "Auto-report dev progress",
          zh: "自动报告开发进度",
        },
        description: {
          ja: "「毎週金曜にこのリポジトリの週次レポートを作って」と設定すれば、マージされたPR数、クローズされたIssue数、まだオープンな課題をまとめたレポートが自動で届きます。\n\n開発リーダーやPMが進捗確認のためにGitHubをチェックする手間がなくなります。Slackに自動投稿すれば、チーム全員が最新状況を把握できます。",
          en: "Set 'Create a weekly report for this repo every Friday' — merged PRs, closed issues, remaining open items summarized automatically.\n\nDev leads and PMs no longer need to check GitHub manually. Auto-post to Slack for team-wide visibility.",
          zh: "设置'每周五为这个仓库创建周报'——自动总结合并的PR、关闭的Issue、待处理的项目。",
        },
      },
    ],
    tools: ["GitHub", "Slack", "Linear"],
  },
  {
    slug: "weather-morning-brief",
    icon: CloudSun,
    title: {
      ja: "朝のブリーフに天気と交通情報も入れる",
      en: "Add Weather & Traffic to Your Morning Brief",
      zh: "在早间简报中加入天气和交通信息",
    },
    description: {
      ja: "仕事の予定だけじゃなく、天気予報や交通情報も朝のブリーフに。出勤前に必要な情報がすべて1つにまとまる。",
      en: "Not just work tasks — add weather and traffic to your morning brief. Everything you need before heading out, in one place.",
      zh: "不只是工作任务——在早间简报中加入天气和交通信息。出门前需要的信息全在一处。",
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "「天気も入れて」と伝える",
          en: "'Add weather too'",
          zh: "'也加上天气'",
        },
        description: {
          ja: "既存の朝のブリーフに「天気予報と交通情報も入れて」と追加するだけ。住んでいるエリアや通勤ルートを伝えておけば、毎朝あなたに必要な情報を自動で集めてくれます。\n\n「雨の日はリモートにしたい」「電車遅延があったらSlackで教えて」など、天気や交通に連動した条件付き通知も設定できます。",
          en: "Just add 'Include weather and traffic info' to your existing morning brief. Tell Sophy your area and commute route, and it auto-gathers what you need each morning.\n\n'I want to work remote on rainy days' or 'Notify me on Slack if trains are delayed' — conditional notifications based on weather/traffic too.",
          zh: "只需在现有的早间简报中添加'加入天气和交通信息'。告诉Sophy你的地区和通勤路线。",
        },
      },
      {
        title: {
          ja: "SophyがWebで最新情報を収集",
          en: "Sophy gathers latest info",
          zh: "Sophy收集最新信息",
        },
        description: {
          ja: "毎朝設定した時刻に、Sophyが天気予報サイトと交通情報をWeb検索でチェック。タスク情報（Linear、Slack）と一緒にまとめて1つのブリーフにしてくれます。\n\n「今日は最高気温32度、午後から雷雨の予報。傘を忘れずに」「中央線で15分の遅延発生中。早めの出発をおすすめ」のように、具体的で実用的な情報を届けます。",
          en: "At your set time each morning, Sophy checks weather sites and traffic info via web search. Combined with task info (Linear, Slack) into one brief.\n\n'High of 32°C today, thunderstorms in the afternoon. Don't forget your umbrella.' 'Central line delayed 15 minutes. Leave early.'",
          zh: "每天早上在设定时间，Sophy通过网络搜索检查天气和交通信息。与任务信息合并为一份简报。",
        },
      },
      {
        title: {
          ja: "生活と仕事を1つのブリーフで",
          en: "Life + work in one brief",
          zh: "生活和工作一份简报",
        },
        description: {
          ja: "仕事のタスク、メール、Slackのメンション、天気、交通——朝起きてSophyのブリーフを見るだけで、今日1日の全体像がわかります。\n\nパートナーがあなたの1日を一緒に準備してくれる感覚です。",
          en: "Work tasks, emails, Slack mentions, weather, traffic — just check Sophy's brief when you wake up to see your whole day.\n\nLike a partner helping you prepare for your day.",
          zh: "工作任务、邮件、Slack提及、天气、交通——起床后看Sophy的简报就能了解全天概况。",
        },
      },
    ],
    tools: ["天気", "Slack", "Linear", "Gmail"],
  },
  {
    slug: "knowledge-base",
    icon: BookOpen,
    title: {
      ja: "社内ナレッジベースをAIで検索・活用する",
      en: "Search & Leverage Your Knowledge Base with AI",
      zh: "用AI搜索和利用内部知识库",
    },
    description: {
      ja: "NotionやGoogle Driveに散在する社内ドキュメントを、Sophyが横断検索。「あのルールどこに書いてあったっけ？」がなくなる。",
      en: "Sophy cross-searches internal docs scattered across Notion and Google Drive. No more 'Where was that rule written again?'",
      zh: "Sophy跨平台搜索散布在Notion和Google Drive中的内部文档。再也不用问'那个规则写在哪了？'",
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "ドキュメントツールを連携する",
          en: "Connect your doc tools",
          zh: "连接文档工具",
        },
        description: {
          ja: "NotionとGoogle Driveを設定画面から連携するだけ。社内のドキュメント、議事録、マニュアル、ガイドラインをSophyがまるごと検索できるようになります。\n\n新しいドキュメントが追加されれば自動で検索対象に。「経費精算のルールは？」「リモートワークの申請方法は？」——社内ルールの確認がチャットで完結します。",
          en: "Just connect Notion and Google Drive from settings. Sophy can now search all your internal docs, meeting notes, manuals, and guidelines.\n\nNew documents auto-added to search. 'What's the expense policy?' 'How do I apply for remote work?' — company rules answered in chat.",
          zh: "从设置连接Notion和Google Drive。Sophy就能搜索所有内部文档。",
        },
      },
      {
        title: {
          ja: "自然な言葉で聞くだけ",
          en: "Just ask naturally",
          zh: "自然语言提问即可",
        },
        description: {
          ja: "「新入社員のオンボーディング手順は？」「デザインレビューのチェックリストを見せて」「去年の忘年会の幹事マニュアルってある？」——普通の言葉で聞けば、Sophyが関連ドキュメントを見つけて内容を要約してくれます。\n\nファイル名やフォルダ構成を覚えてなくても大丈夫。Sophyが文脈を理解して最適なドキュメントを見つけ出します。日本語の同義語や略語も自動展開するので、検索漏れも最小限に。",
          en: "'What's the onboarding process for new hires?' 'Show me the design review checklist' 'Is there a year-end party organizer manual?' — ask in plain language and Sophy finds and summarizes relevant docs.\n\nNo need to remember file names. Sophy understands context and finds the best match.",
          zh: "'新员工入职流程是什么？''给我看设计审查清单'——用普通话问，Sophy就能找到并总结相关文档。",
        },
      },
      {
        title: {
          ja: "チーム全員のナレッジアクセスを底上げ",
          en: "Level up team knowledge access",
          zh: "提升全员知识获取能力",
        },
        description: {
          ja: "Sophyを社内ナレッジの窓口にすれば、「〇〇さんに聞かないとわからない」属人化が解消されます。Slackから質問してもLINEから質問しても、同じナレッジにアクセスできます。\n\nよく聞かれる質問はSophyが自動で学習。「最近チームで一番検索されてるのはこのドキュメント」といったインサイトも出せます。新人の立ち上がりが早くなり、ベテランの「同じ質問に答える」時間がなくなります。",
          en: "Make Sophy the gateway to team knowledge — no more 'only person X knows.' Access the same knowledge from Slack or LINE.\n\nSophy auto-learns frequently asked questions. 'The most searched doc this week is X.' New hires ramp up faster, veterans stop answering the same questions.",
          zh: "让Sophy成为团队知识的入口——不再'只有某某知道'。从Slack或LINE都能访问相同的知识。",
        },
      },
    ],
    tools: ["Notion", "Google Drive", "Slack"],
  },
  {
    slug: "health-tracker",
    icon: Heart,
    title: {
      ja: "体調・コンディションを記録して傾向を把握する",
      en: "Track Health & Condition Trends",
      zh: "记录身体状况并把握趋势",
    },
    description: {
      ja: "毎日の体調、睡眠時間、気分をSophyに報告するだけ。週次で傾向をまとめてくれるので、パフォーマンスの波を可視化できる。",
      en: "Just report daily condition, sleep, and mood to Sophy. Weekly trend summaries help you visualize performance patterns.",
      zh: "只需每天向Sophy报告身体状况、睡眠和心情。每周趋势总结帮你可视化表现模式。",
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "毎日チャットで報告するだけ",
          en: "Just chat daily",
          zh: "每天聊天报告即可",
        },
        description: {
          ja: "「今日は6時間睡眠、体調まあまあ、ちょっと肩こり」——毎日1行送るだけでOK。フォーマットも決まってないので、好きな言い方で大丈夫です。\n\nSophyが内容を解析して、睡眠時間・体調・症状・気分を自動で分類・記録してくれます。あなたの健康管理パートナーとして、毎日の小さな変化を見逃しません。",
          en: "'Slept 6 hours, feeling OK, slight stiff shoulders' — just one line daily. No fixed format needed.\n\nSophy auto-analyzes and records sleep, condition, symptoms, and mood. Your health management partner that never misses small changes.",
          zh: "'睡了6小时，状态还行，有点肩酸'——每天一行就够了。不需要固定格式。",
        },
      },
      {
        title: {
          ja: "Sophyが記録を蓄積する",
          en: "Sophy accumulates records",
          zh: "Sophy积累记录",
        },
        description: {
          ja: "毎日の報告をSophyのメモリに蓄積。あなたの体調パターンを日々学習していきます。\n\n「先週より睡眠時間が1時間減ってるけど大丈夫？」「肩こりが3日続いてるね」と、Sophyがさりげなく気づいてくれることも。パートナーだからこそ、あなたの小さな変化に気づけます。",
          en: "Daily reports stored in Sophy's memory, learning your condition patterns day by day.\n\n'Your sleep is 1 hour less than last week — are you OK?' 'Stiff shoulders for 3 days now.' Sophy notices small changes because it's your partner.",
          zh: "每日报告存储在Sophy的记忆中，每天学习你的身体状况模式。",
        },
      },
      {
        title: {
          ja: "週次レポートで傾向が見える",
          en: "Weekly report reveals trends",
          zh: "周报显示趋势",
        },
        description: {
          ja: "「毎週日曜に今週のコンディションレポートを作って」と設定すれば、睡眠時間の推移、体調の波、よくある症状のパターンが可視化されたレポートが届きます。\n\n「火曜と水曜にパフォーマンスが落ちやすい」「睡眠7時間以上の日は体調良好の傾向」のようなインサイトを発見できます。データに基づいた自己管理で、ビジネスパーソンのパフォーマンスを最大化します。",
          en: "Set 'Create a condition report every Sunday' — sleep trends, condition patterns, common symptoms visualized.\n\n'Performance tends to dip on Tue/Wed.' 'Days with 7+ hours sleep correlate with good condition.' Data-driven self-management for peak performance.",
          zh: "设置'每周日创建一份状态报告'——睡眠趋势、身体状况模式、常见症状可视化。",
        },
      },
    ],
    tools: ["Sophy", "Slack"],
  },
  {
    slug: "spotify-focus-mode",
    icon: Music,
    title: {
      ja: "集中モードのBGMをAIにお任せ",
      en: "Let AI Choose Your Focus Mode BGM",
      zh: "让AI选择你的专注模式BGM",
    },
    description: {
      ja: "「集中したいからBGM流して」「リラックスできる曲をかけて」——Sophyがあなたの好みと状況に合わせてSpotifyを操作。",
      en: "'I need to focus, play some BGM' 'Play something relaxing' — Sophy controls Spotify based on your preferences and context.",
      zh: "'我需要集中，播放BGM''放一些放松的音乐'——Sophy根据你的偏好和场景控制Spotify。",
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "「集中したい」と伝える",
          en: "Say 'I need to focus'",
          zh: "说'我需要集中'",
        },
        description: {
          ja: "「今から2時間集中したいからBGMかけて」「カフェジャズみたいな曲をお願い」——Sophyに伝えるだけで、Spotifyのプレイリストを自動再生してくれます。\n\nあなたの好みをSophyが学習していくので、使うほど「わかってる」選曲になります。「前にかけてくれたあのプレイリスト良かったからまた流して」も通じます。",
          en: "'Play BGM, I want to focus for 2 hours.' 'Something like café jazz.' Sophy auto-plays Spotify playlists.\n\nSophy learns your preferences — music choices improve over time. 'Play that playlist from before, it was great' works too.",
          zh: "'播放BGM，我想集中2小时。''类似咖啡爵士的音乐。'Sophy自动播放Spotify播放列表。",
        },
      },
      {
        title: {
          ja: "状況に合わせて自動切替",
          en: "Auto-switch by context",
          zh: "根据场景自动切换",
        },
        description: {
          ja: "Google Calendarと連携すれば、「会議15分前にBGMを止めて」「会議後は集中BGMに戻して」と自動で切り替えることも。\n\n朝は軽めのBGMから始めて、午後の集中タイムにはテンポを上げる——1日のリズムに合わせた自動BGMスケジュールも組めます。",
          en: "Link with Google Calendar: 'Stop BGM 15 minutes before meetings' and 'Resume focus BGM after.' Auto-switch based on your schedule.\n\nLight BGM in the morning, pick up tempo for afternoon focus — build an auto BGM schedule matching your day's rhythm.",
          zh: "与Google Calendar联动：'会议前15分钟停止BGM''会议后恢复专注BGM'。",
        },
      },
      {
        title: {
          ja: "チームでも使える",
          en: "Works for teams too",
          zh: "团队也能用",
        },
        description: {
          ja: "Slackで「@sophy 今からもくもく会BGMかけて」とチーム全員で使うことも。オフィスのBGM管理をSophyに任せれば、「誰が音楽変えた？」問題も解決。\n\nチーム機能があるSophyならでは。個人の好みも、チームの雰囲気も、両方わかってくれるパートナーです。",
          en: "Use in Slack: '@sophy play focused work BGM for the team.' Let Sophy manage office BGM — solves 'who changed the music?' debates.\n\nUnique to Sophy with team features. A partner that understands both personal preferences and team vibes.",
          zh: "在Slack中使用：'@sophy 给团队播放专注工作BGM'。让Sophy管理办公室BGM。",
        },
      },
    ],
    tools: ["Spotify", "Google Calendar", "Slack"],
  },
  {
    slug: "secure-workflow",
    icon: Shield,
    title: {
      ja: "パスワードや認証情報を安全に扱う",
      en: "Handle Passwords & Credentials Securely",
      zh: "安全处理密码和认证信息",
    },
    description: {
      ja: "ブラウザ自動化で外部サービスにログインする際も、1Passwordと連携して認証情報を安全に管理。",
      en: "Even when automating browser logins, credentials stay secure with 1Password integration.",
      zh: "即使在自动化浏览器登录时，通过1Password集成安全管理认证信息。",
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "1Passwordに認証情報を保存",
          en: "Store credentials in 1Password",
          zh: "在1Password中保存认证信息",
        },
        description: {
          ja: "Sophyがブラウザ自動化で外部サービス（TikTok、Instagram、社内ツールなど）にログインする際、パスワードを平文で保存する必要はありません。1Passwordに保存された認証情報を安全に参照します。\n\nSophyにパスワードを直接教える必要がないので、セキュリティリスクを最小限に。チームで使う場合も、各メンバーの認証情報は本人の1Passwordから参照されます。",
          en: "When Sophy automates browser logins (TikTok, Instagram, internal tools), no need to store passwords in plaintext. Sophy securely references credentials from 1Password.\n\nNo need to tell Sophy your password directly — minimal security risk. For teams, each member's credentials are referenced from their own 1Password.",
          zh: "Sophy自动化浏览器登录时，无需明文存储密码。安全引用1Password中的认证信息。",
        },
      },
      {
        title: {
          ja: "MFAも自動処理",
          en: "Auto-handle MFA",
          zh: "自动处理MFA",
        },
        description: {
          ja: "二段階認証（MFA）が必要なサービスでも、1PasswordのTOTPコード（ワンタイムパスワード）を自動入力。手動でコードを入力する手間がなくなります。\n\nセキュリティを犠牲にせずに自動化を実現。食料品の注文やサブスクの管理など、MFA付きのサービスも完全自動化できます。",
          en: "Even for services requiring two-factor auth, Sophy auto-enters 1Password TOTP codes. No manual code entry needed.\n\nAutomate without sacrificing security. Even MFA-protected services like grocery orders and subscription management can be fully automated.",
          zh: "即使需要双因素认证的服务，Sophy也能自动输入1Password的TOTP代码。",
        },
      },
      {
        title: {
          ja: "監査ログで安全を確認",
          en: "Audit logs for safety",
          zh: "审计日志确保安全",
        },
        description: {
          ja: "Sophyがどのサービスにいつアクセスしたかは、すべてログに記録されます。「昨日Sophyがアクセスしたサービスは？」と聞けば確認可能。\n\n企業のセキュリティポリシーに合わせて、「このサービスへの自動ログインは禁止」「アクセス前に承認が必要」といった制限も設定できます。安心して自動化を拡大できます。",
          en: "Every service Sophy accesses is logged. 'What did Sophy access yesterday?' — instant audit.\n\nSet restrictions per your security policy: 'Block auto-login to this service' or 'Require approval before access.' Scale automation with confidence.",
          zh: "Sophy访问的每个服务都有日志记录。可以设置安全策略限制。",
        },
      },
    ],
    tools: ["1Password", "Sophy"],
  },
  {
    slug: "client-crm",
    icon: Users,
    title: {
      ja: "取引先の情報をチャットで管理する",
      en: "Manage Client Info via Chat",
      zh: "通过聊天管理客户信息",
    },
    description: {
      ja: "取引先との過去のやりとり、担当者情報、商談履歴をSophyが記憶。「あの会社の最後の連絡いつだっけ？」にすぐ答えてくれる。",
      en: "Sophy remembers past interactions, contacts, and deal history. 'When was our last contact with that company?' — instant answer.",
      zh: "Sophy记住过去的往来、联系人和商谈历史。'我们最后一次联系那家公司是什么时候？'——立即回答。",
    },
    category: "sales",
    steps: [
      {
        title: {
          ja: "商談後にSophyにメモを共有",
          en: "Share notes after meetings",
          zh: "商谈后分享笔记",
        },
        description: {
          ja: "「今日のA社との商談メモ。担当は田中部長、予算500万、来月中に決裁予定」——商談が終わるたびにSophyに送るだけ。Sophyがメモリに記録して蓄積してくれます。\n\nかしこまったフォーマットは不要。会話の流れでそのまま伝えればOK。Slackからでも、LINEからでも、移動中にサッとメモを残せます。",
          en: "'Meeting notes with Company A. Contact: Mr. Tanaka, budget ¥5M, decision expected next month.' Just send after each meeting.\n\nNo formal format needed. Share naturally from Slack, LINE, or web — quick notes on the go.",
          zh: "'A公司商谈记录。负责人田中部长，预算500万，预计下月决裁。'每次商谈后发给Sophy就行。",
        },
      },
      {
        title: {
          ja: "「A社の状況教えて」で即確認",
          en: "'Tell me about Company A'",
          zh: "'告诉我A公司的情况'",
        },
        description: {
          ja: "次の商談前に「A社の過去のやりとりまとめて」と聞くだけ。これまでの商談履歴、担当者情報、決裁状況、過去の課題をSophyが時系列でまとめてくれます。\n\nGmailやSlackのやりとりも含めて横断検索できるので、「前回のメールで何を約束したっけ？」もすぐわかります。高額なCRMツールを導入しなくても、Sophyがあなたの営業パートナーとして情報を管理してくれます。",
          en: "'Summarize our history with Company A' before the next meeting. Sophy compiles deal history, contacts, status, and past issues chronologically.\n\nCross-searches Gmail and Slack too. No expensive CRM needed — Sophy manages your sales info as your partner.",
          zh: "下次商谈前问'总结一下我们和A公司的历史'。Sophy按时间线整理商谈历史、联系人、状态。",
        },
      },
      {
        title: {
          ja: "フォローアップも自動リマインド",
          en: "Auto follow-up reminders",
          zh: "自动跟进提醒",
        },
        description: {
          ja: "「A社には来週月曜にフォロー連絡する」と伝えておけば、月曜の朝にSophyがリマインドしてくれます。メールの下書きも一緒に準備してくれるので、すぐにフォローに入れます。\n\n「2週間連絡していない取引先があったら教えて」と自動チェックを設定しておけば、フォロー漏れもゼロに。高額なCRMを導入しなくても、Sophyが営業パートナーとして取引先管理を支えてくれます。",
          en: "'Follow up with Company A next Monday.' Sophy reminds you Monday morning with a draft email ready.\n\n'Notify me of clients we haven't contacted in 2 weeks' — zero missed follow-ups. No expensive CRM needed — Sophy manages client relationships as your sales partner.",
          zh: "'下周一跟进A公司'。Sophy周一早上提醒你并准备好邮件草稿。",
        },
      },
    ],
    tools: ["Sophy", "Gmail", "Slack"],
  },
  {
    slug: "meeting-notes-auto",
    icon: FileText,
    title: {
      ja: "議事録を自動で作成・配信する",
      en: "Auto-Generate & Distribute Meeting Notes",
      zh: "自动生成并分发会议记录",
    },
    description: {
      ja: "会議が終わったらSophyに「議事録まとめて」と送るだけ。参加者への配信まで全自動。",
      en: "Just tell Sophy 'Summarize the meeting' and it handles everything from notes to distribution.",
      zh: "会议结束后只需告诉Sophy'总结会议'，从记录到分发全自动完成。",
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "会議の内容をSophyに共有",
          en: "Share meeting content with Sophy",
          zh: "将会议内容分享给Sophy",
        },
        description: {
          ja: "会議が終わったらメモや録音テキストをSophyに共有。「さっきの会議まとめて」だけでもOK。Sophyがチャットの会話から要点を抽出してくれます。\n\nかしこまったフォーマットは不要。箇条書きでも、走り書きでも、そのまま送ればSophyが意味を汲み取ってくれます。",
          en: "Share your notes or transcription after the meeting. Even 'Summarize the last meeting' is enough — Sophy extracts key points from the conversation.",
          zh: "会议结束后分享笔记或录音文字。只说'总结刚才的会议'也行，Sophy会从对话中提取要点。",
        },
      },
      {
        title: {
          ja: "Sophyが議事録を作成",
          en: "Sophy creates meeting notes",
          zh: "Sophy生成会议记录",
        },
        description: {
          ja: "決定事項、アクションアイテム、担当者、期限を自動で整理。会議のたびにフォーマットを揃えてくれるので、読みやすさも統一されます。\n\n「ここの表現変えて」「担当者追加して」など、チャットで修正指示もできます。何度でも調整できるので、納得いくまで仕上げられます。",
          en: "Automatically organizes decisions, action items, owners, and deadlines. Consistent formatting across all meetings for easy readability.",
          zh: "自动整理决定事项、行动项、负责人和截止日期。每次会议格式统一，便于阅读。",
        },
      },
      {
        title: {
          ja: "参加者に自動配信",
          en: "Auto-distribute to participants",
          zh: "自动分发给参与者",
        },
        description: {
          ja: "完成した議事録をSlackの会議チャネルに自動投稿。Notionにも自動保存されるので、後から探す手間もありません。\n\n「毎週の定例会議後に自動で議事録を配信」の設定も可能。会議が終わったら何もしなくても、参加者全員に議事録が届く仕組みを作れます。",
          en: "Auto-posts to the meeting's Slack channel and saves to Notion. Set up recurring auto-distribution for weekly meetings — zero effort after setup.",
          zh: "自动发布到Slack会议频道并保存到Notion。可设置每周例会后自动分发。",
        },
      },
    ],
    tools: ["Slack", "Notion"],
  },
  {
    slug: "multilingual-support",
    icon: Globe,
    title: {
      ja: "海外とのやりとりをリアルタイム翻訳する",
      en: "Real-Time Translation for International Communication",
      zh: "实时翻译国际沟通",
    },
    description: {
      ja: "英語のメール、中国語のドキュメント、韓国語のSlackメッセージ。Sophyがリアルタイムで翻訳してくれる。",
      en: "English emails, Chinese documents, Korean Slack messages — Sophy translates everything in real time.",
      zh: "英文邮件、中文文档、韩文Slack消息——Sophy实时翻译一切。",
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "「これ翻訳して」と送るだけ",
          en: "Just say 'Translate this'",
          zh: "只需说'翻译这个'",
        },
        description: {
          ja: "メールの本文、Slackのメッセージ、ドキュメントの一部——何でもSophyに送れば翻訳してくれます。「ビジネス調で」「カジュアルに」などトーンの指定も可能。\n\n言語を指定しなくても、Sophyが自動で判定して日本語に翻訳。もちろん「英語にして」「中国語にして」と指定もできます。",
          en: "Send any text — emails, Slack messages, document excerpts — and Sophy translates it. Specify tone like 'formal' or 'casual' for perfect results.",
          zh: "发送任何文本——邮件、Slack消息、文档片段——Sophy就会翻译。可指定语调如'正式'或'休闲'。",
        },
      },
      {
        title: {
          ja: "返信の下書きも外国語で",
          en: "Draft replies in foreign languages",
          zh: "用外语起草回复",
        },
        description: {
          ja: "「このメールに英語で返信書いて」と頼めば、あなたの意図を汲んだ外国語の返信を作成。ニュアンスの調整もチャットで「もう少し丁寧に」と指示できます。\n\n翻訳ツールでは伝わらない微妙なニュアンスも、Sophyなら文脈を理解して自然な表現に。ビジネスメールも、チャットの返信も、あなたの言いたいことをそのまま外国語にしてくれます。",
          en: "'Write a reply to this email in English' — Sophy drafts a response that captures your intent. Fine-tune with 'Make it more polite' right in the chat.",
          zh: "'用英语回复这封邮件'——Sophy会起草一封传达你意图的回复。可以在聊天中微调语气。",
        },
      },
      {
        title: {
          ja: "定期的な翻訳も自動化",
          en: "Automate recurring translations",
          zh: "自动化定期翻译",
        },
        description: {
          ja: "「海外チームからのSlackメッセージを毎朝まとめて日本語に翻訳して」と設定すれば、言語の壁を感じずにチーム間コミュニケーションが取れます。\n\nグローバルチームとの協業がもっとスムーズに。翻訳にかけていた時間を、本来の仕事に使えるようになります。",
          en: "'Translate overseas team Slack messages into Japanese every morning' — break down language barriers for seamless cross-team communication.",
          zh: "'每天早上把海外团队的Slack消息翻译成日语'——消除语言障碍，实现无缝跨团队沟通。",
        },
      },
    ],
    tools: ["Slack", "Gmail", "Notion"],
  },
  {
    slug: "expense-tracking",
    icon: FileText,
    title: {
      ja: "家計簿・経費を会話で記録する",
      en: "Track Expenses Through Chat",
      zh: "通过聊天记录支出",
    },
    description: {
      ja: "「今日ランチ980円」「タクシー3200円」と送るだけ。Sophyが自動で分類・記録して月次レポートも作ってくれる。",
      en: "Just say 'Lunch ¥980' or 'Taxi ¥3200' — Sophy auto-categorizes, records, and generates monthly reports.",
      zh: "只需说'午饭980日元''出租车3200日元'——Sophy自动分类记录并生成月度报告。",
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "使ったらSophyに報告するだけ",
          en: "Just tell Sophy when you spend",
          zh: "花钱后告诉Sophy就行",
        },
        description: {
          ja: "「今日ランチ980円」「コンビニ450円」——使ったタイミングでSophyにチャットで送るだけ。フォーマット不要、好きな言い方でOK。\n\nレシートの写真を撮って送ることもできます。Sophyが金額と内容を読み取って記録してくれるので、入力の手間はゼロ。",
          en: "'Lunch ¥980 today' or 'Convenience store ¥450' — just chat with Sophy whenever you spend. No format required, any wording works.",
          zh: "'今天午饭980日元''便利店450日元'——花钱时随时告诉Sophy。无需格式，随便说。",
        },
      },
      {
        title: {
          ja: "Sophyが自動で分類・記録",
          en: "Sophy auto-categorizes and records",
          zh: "Sophy自动分类记录",
        },
        description: {
          ja: "金額、カテゴリ（食費、交通費、交際費など）、日付を自動で判定して記録。「先週の食費いくら？」と聞けばすぐ答えてくれます。\n\n間違って記録された場合も「昨日のランチ、980円じゃなくて890円だった」と伝えれば修正してくれます。自然な会話でデータの管理ができます。",
          en: "Auto-detects amount, category (food, transport, entertainment, etc.), and date. Ask 'How much did I spend on food last week?' for instant answers.",
          zh: "自动识别金额、类别（餐饮、交通、社交等）和日期。问'上周吃饭花了多少？'立即回答。",
        },
      },
      {
        title: {
          ja: "月末にレポートが届く",
          en: "Monthly report delivered",
          zh: "月底收到报告",
        },
        description: {
          ja: "「毎月1日に先月の支出レポートを作って」と設定すれば、カテゴリ別の集計、前月比較、使いすぎアラートをまとめたレポートが届きます。\n\nルーズな自分でも続けられる家計管理。面倒な入力作業や集計作業はSophyに任せて、お金の使い方を見直す時間に使えます。",
          en: "'Create a spending report on the 1st of each month' — get category breakdowns, month-over-month comparisons, and overspending alerts automatically.",
          zh: "'每月1号生成上月支出报告'——自动获取类别汇总、环比对比和超支提醒。",
        },
      },
    ],
    tools: ["Sophy", "Slack"],
  },
  {
    slug: "learning-notes",
    icon: BookOpen,
    title: {
      ja: "学習メモ・読書ノートをAIで整理する",
      en: "Organize Learning Notes with AI",
      zh: "用AI整理学习笔记",
    },
    description: {
      ja: "読んだ記事、見た動画、勉強したこと。Sophyに共有すると自動でタグ付け・整理して、いつでも検索できるナレッジに。",
      en: "Articles, videos, study notes — share with Sophy for auto-tagging and organization into searchable knowledge.",
      zh: "文章、视频、学习内容——分享给Sophy，自动标签整理成可搜索的知识库。",
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "気になった情報をSophyに送る",
          en: "Send interesting info to Sophy",
          zh: "将感兴趣的信息发给Sophy",
        },
        description: {
          ja: "「この記事面白かった」とURLを送ったり、「今日学んだこと：Reactのuseeffectの〜」とメモを送ったり。形式は自由。\n\nSophyは送られた内容を読み取って、重要なポイントを抽出してくれます。あなたは「面白い」と思ったものをどんどん送るだけ。",
          en: "Share article URLs, video notes, or study memos in any format. Sophy reads and extracts key points automatically.",
          zh: "分享文章URL、视频笔记或学习备忘，格式随意。Sophy自动阅读并提取关键点。",
        },
      },
      {
        title: {
          ja: "Sophyが整理・タグ付けする",
          en: "Sophy organizes and tags",
          zh: "Sophy整理并打标签",
        },
        description: {
          ja: "送った内容をカテゴリ分け（技術、ビジネス、デザインなど）して、キーワードをタグ付け。Sophyのメモリに蓄積されていきます。\n\n手動でフォルダ分けしたり、タグを考えたりする必要はありません。Sophyが文脈を理解して、最適な整理をしてくれます。",
          en: "Categorizes content (tech, business, design, etc.) and adds keyword tags. Everything accumulates in Sophy's memory — no manual organizing needed.",
          zh: "按类别（技术、商业、设计等）分类并添加关键词标签。一切都积累在Sophy的记忆中。",
        },
      },
      {
        title: {
          ja: "「あの話なんだっけ？」にすぐ答えてくれる",
          en: "'What was that thing about...?' — instant recall",
          zh: "'那个事情是什么来着？'——立即回忆",
        },
        description: {
          ja: "「先月読んだReactの記事ってなんだっけ」「型安全について学んだことまとめて」——Sophyが蓄積したナレッジから関連情報を検索・要約。あなただけのセカンドブレインに。\n\n勉強したことを忘れてしまっても、Sophyが覚えていてくれます。必要な時にいつでも引き出せる、あなた専用の知識ベースです。",
          en: "'What was that React article from last month?' 'Summarize what I learned about type safety' — Sophy searches your accumulated knowledge and delivers instant summaries. Your personal second brain.",
          zh: "'上个月读的React文章是什么？''总结我学到的类型安全知识'——Sophy从积累的知识中搜索并即时总结。你的个人第二大脑。",
        },
      },
    ],
    tools: ["Sophy", "Notion"],
  },
  {
    slug: "travel-planning",
    icon: Globe,
    title: {
      ja: "旅行プランをAIと一緒に組む",
      en: "Plan Trips Together with AI",
      zh: "与AI一起规划旅行",
    },
    description: {
      ja: "「来月3泊で京都旅行したい」と伝えるだけ。予算、好み、過去の会話を踏まえてSophyが最適プランを提案。",
      en: "Just say 'I want a 3-night Kyoto trip next month' — Sophy plans based on your budget, preferences, and past conversations.",
      zh: "只需说'下个月想去京都3晚'——Sophy根据预算、喜好和过去对话推荐最佳方案。",
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "ざっくり希望を伝える",
          en: "Share your rough preferences",
          zh: "大致说说你的想法",
        },
        description: {
          ja: "「来月の3連休で京都行きたい」「予算は1人5万くらい」「寺社よりカフェ巡りがいい」——希望をざっくり伝えるだけ。Sophyがあなたの好みも覚えているので、過去の旅行の感想も参考にしてくれます。\n\n行き先が決まっていなくても大丈夫。「週末どこか行きたい」から一緒に考えてくれます。",
          en: "'Kyoto for the long weekend' 'Budget ¥50K per person' 'Prefer cafes over temples' — share rough ideas. Sophy remembers your preferences from past trips too.",
          zh: "'下个月三连休想去京都''预算每人5万日元左右''比起寺庙更喜欢咖啡店巡游'——大致说说想法就行。",
        },
      },
      {
        title: {
          ja: "Sophyがプランを組んでくれる",
          en: "Sophy creates your itinerary",
          zh: "Sophy制定行程",
        },
        description: {
          ja: "Web検索で観光スポット、レストラン、ホテルを調べて、日程に合わせたプランを提案。「1日目は嵐山エリア、2日目は東山」のようにルートも考慮してくれます。\n\n「もう少し安いホテルにして」「2日目の午後は空けておいて」など、チャットで何度でも調整できます。",
          en: "Sophy searches for spots, restaurants, and hotels, then builds a day-by-day plan with routes like 'Day 1: Arashiyama, Day 2: Higashiyama.'",
          zh: "Sophy搜索景点、餐厅和酒店，按日程制定路线计划，如'第一天岚山，第二天东山'。",
        },
      },
      {
        title: {
          ja: "予約までサポート",
          en: "Booking support included",
          zh: "支持预订",
        },
        description: {
          ja: "気に入ったプランのホテルやレストランは、ブラウザ自動化で予約サイトから予約もサポート。旅程はNotionに保存して、旅行中もSophyに「今日の予定は？」と聞けます。\n\n旅行の計画から予約、当日のガイドまで。Sophyがあなたの旅行パートナーとして、旅を丸ごとサポートします。",
          en: "Sophy helps book hotels and restaurants via browser automation. Itinerary saved to Notion — ask 'What's today's plan?' during your trip.",
          zh: "Sophy通过浏览器自动化帮助预订酒店和餐厅。行程保存到Notion，旅途中随时问'今天的安排是什么？'",
        },
      },
    ],
    tools: ["Sophy", "Notion"],
  },
  {
    slug: "recruitment-research",
    icon: Users,
    title: {
      ja: "採用候補者をAIでリサーチする",
      en: "AI-Powered Candidate Research",
      zh: "用AI调研候选人",
    },
    description: {
      ja: "求人に応募があったら、候補者のSNSやポートフォリオをSophyが自動リサーチ。面接前の準備時間を大幅短縮。",
      en: "When applications come in, Sophy auto-researches candidates' profiles and portfolios, dramatically cutting interview prep time.",
      zh: "收到求职申请后，Sophy自动调研候选人的社交媒体和作品集，大幅缩短面试准备时间。",
    },
    category: "sales",
    steps: [
      {
        title: {
          ja: "候補者情報をSophyに共有",
          en: "Share candidate info with Sophy",
          zh: "将候选人信息分享给Sophy",
        },
        description: {
          ja: "「この人のLinkedInとGitHubをチェックして」と名前やURLを共有。Sophyがブラウザで候補者のプロフィール、投稿、リポジトリを調査します。\n\n履歴書のPDFを送っても、Sophyが内容を読み取って追加リサーチを行ってくれます。",
          en: "'Check this person's LinkedIn and GitHub' — share names or URLs. Sophy investigates profiles, posts, and repositories via browser.",
          zh: "'查看这个人的LinkedIn和GitHub'——分享姓名或URL。Sophy通过浏览器调查个人资料、帖子和代码库。",
        },
      },
      {
        title: {
          ja: "Sophyがレポートを作成",
          en: "Sophy generates a report",
          zh: "Sophy生成报告",
        },
        description: {
          ja: "経歴、スキル、活動履歴、発信内容をまとめたレポートを自動生成。「この人はフロントエンドが強み、最近はAI関連に興味を持っている様子」のようにインサイトも提供します。\n\n複数の候補者を比較したい場合も、「この3人を比較して」と頼めば一覧表にまとめてくれます。",
          en: "Auto-generates a report covering career, skills, activity history, and content. Provides insights like 'Strong in frontend, recently interested in AI.'",
          zh: "自动生成涵盖经历、技能、活动历史和内容的报告。提供如'前端很强，最近对AI感兴趣'的洞察。",
        },
      },
      {
        title: {
          ja: "面接準備が5分で完了",
          en: "Interview prep done in 5 minutes",
          zh: "5分钟完成面试准备",
        },
        description: {
          ja: "レポートをもとに「この候補者への質問案」も提案。採用担当が10人分のリサーチに半日かけていたのが、Sophyなら1人あたり5分。スカウトメールの下書きも作成可能。\n\n採用プロセスの効率化だけでなく、候補者一人ひとりに合わせた質問ができるので、面接の質も上がります。",
          en: "Suggests interview questions based on the report. What used to take half a day for 10 candidates now takes 5 minutes each. Scout emails can be drafted too.",
          zh: "根据报告建议面试问题。以前10个候选人要花半天，现在每人只需5分钟。还能起草招聘邮件。",
        },
      },
    ],
    tools: ["Sophy", "Gmail", "Slack"],
  },
  {
    slug: "discord-community",
    icon: MessageSquare,
    title: {
      ja: "Discordコミュニティの運営をAIで効率化する",
      en: "Streamline Discord Community Management with AI",
      zh: "用AI高效管理Discord社区",
    },
    description: {
      ja: "メンバーからの質問に自動回答、モデレーション補助、週次の活動サマリー。コミュニティ運営の負担を軽減。",
      en: "Auto-answer member questions, assist moderation, and generate weekly activity summaries to reduce community management burden.",
      zh: "自动回答成员问题、辅助管理、生成每周活动摘要，减轻社区运营负担。",
    },
    category: "management",
    steps: [
      {
        title: {
          ja: "コミュニティのルールやFAQを覚えさせる",
          en: "Teach Sophy your community rules and FAQ",
          zh: "让Sophy记住社区规则和FAQ",
        },
        description: {
          ja: "よくある質問、コミュニティガイドライン、過去の回答例をSophyに共有。Sophyがナレッジとして記憶し、メンバーからの質問に活用します。\n\n一度覚えさせれば、新しい質問パターンもSophyが学習して対応精度が上がっていきます。",
          en: "Share FAQs, community guidelines, and past answer examples. Sophy memorizes them as knowledge and uses them to handle member questions.",
          zh: "分享常见问题、社区准则和过去的回答示例。Sophy将其记忆为知识库，用于回答成员问题。",
        },
      },
      {
        title: {
          ja: "質問にAIが一次回答",
          en: "AI handles first-response to questions",
          zh: "AI进行初步回答",
        },
        description: {
          ja: "メンバーからの質問をSophyが自動検知し、ナレッジベースから回答を作成して提案。モデレーターが確認して投稿するフローで品質も担保します。\n\n回答に自信がない場合はSophyが「これはモデレーターに確認が必要です」とフラグを立ててくれるので、誤回答のリスクも最小限に。",
          en: "Sophy auto-detects questions and drafts answers from the knowledge base. Moderators review before posting to ensure quality.",
          zh: "Sophy自动检测问题并从知识库起草回答。管理员审核后发布以确保质量。",
        },
      },
      {
        title: {
          ja: "週次の活動レポートを自動生成",
          en: "Auto-generate weekly activity reports",
          zh: "自动生成每周活动报告",
        },
        description: {
          ja: "「今週の新規メンバー数」「よく話題になったトピック」「未回答の質問」をまとめた週次レポートをSlackに自動配信。コミュニティの健康状態が一目でわかります。\n\nトレンドの変化や問題の早期発見にも役立ちます。運営チーム全員が同じ情報を共有できるので、判断もスピーディに。",
          en: "Weekly reports covering new members, trending topics, and unanswered questions — auto-delivered to Slack. Community health at a glance.",
          zh: "每周报告涵盖新成员、热门话题和未回答问题——自动推送到Slack。一目了然社区健康状况。",
        },
      },
    ],
    tools: ["Slack", "Notion"],
  },
  {
    slug: "flight-hotel-search",
    icon: Globe,
    title: {
      ja: "航空券・ホテルをAIに探してもらう",
      en: "AI-Powered Flight & Hotel Search",
      zh: "让AI搜索机票和酒店",
    },
    description: {
      ja: "「来月大阪出張、2泊で安いホテル探して」と伝えるだけ。Sophyがブラウザで検索・比較して最適なプランを提案。",
      en: "Just tell Sophy your travel needs. It searches and compares flights and hotels via browser automation.",
      zh: "只需告诉Sophy你的出差需求，它会自动搜索比较航班和酒店。",
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "条件を伝える",
          en: "Share your requirements",
          zh: "告知条件",
        },
        description: {
          ja: "日程、行き先、予算、好みをチャットで伝えるだけ。「羽田発で朝の便がいい」「駅近のビジネスホテル」など自然な言葉でOK。",
          en: "Just chat about your dates, destination, budget, and preferences in natural language.",
          zh: "用自然语言告诉日程、目的地、预算和偏好即可。",
        },
      },
      {
        title: {
          ja: "Sophyが検索・比較する",
          en: "Sophy searches & compares",
          zh: "Sophy搜索比较",
        },
        description: {
          ja: "ブラウザ自動化で複数の旅行サイトを巡回。料金、時間、立地を比較して候補をピックアップ。人間が1時間かけてやる比較作業をSophyが数分で。",
          en: "Browser automation visits multiple travel sites, comparing prices, times, and locations. What takes you an hour, Sophy does in minutes.",
          zh: "浏览器自动化访问多个旅行网站，比较价格、时间和位置。",
        },
      },
      {
        title: {
          ja: "候補を提案してくれる",
          en: "Get curated recommendations",
          zh: "获得精选推荐",
        },
        description: {
          ja: "「この3つがおすすめ。理由は〜」と候補を整理して提示。気に入ったら予約もSophyがブラウザで代行。出張が多い人の時間を劇的に節約。",
          en: "'Here are 3 recommendations with reasons.' If you like one, Sophy can book it via browser too. A huge time-saver for frequent travelers.",
          zh: "'推荐这3个，原因是……'满意的话Sophy还能代为预订。",
        },
      },
    ],
    tools: ["Sophy"],
  },
  {
    slug: "ec-shopping-automation",
    icon: Zap,
    title: {
      ja: "日用品の注文をAIで自動化する",
      en: "Automate Daily Shopping with AI",
      zh: "用AI自动化日用品订购",
    },
    description: {
      ja: "毎月の消耗品、オフィスの備品、定期的な買い物。Sophyがブラウザで注文を自動処理。",
      en: "Monthly supplies, office items, routine purchases — Sophy handles orders automatically via browser.",
      zh: "每月消耗品、办公用品、定期采购，Sophy通过浏览器自动处理订单。",
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "買い物リストをSophyに共有",
          en: "Share your shopping list",
          zh: "分享购物清单",
        },
        description: {
          ja: "「いつものミネラルウォーター24本」「A4コピー用紙5箱」など。一度伝えればSophyが記憶するので、次回からは「いつものやつお願い」でOK。",
          en: "Tell Sophy what you need. It remembers your preferences, so next time just say 'the usual order please.'",
          zh: "告诉Sophy需要什么，它会记住偏好，下次只需说'老样子'。",
        },
      },
      {
        title: {
          ja: "Sophyがブラウザで注文処理",
          en: "Sophy places the order",
          zh: "Sophy自动下单",
        },
        description: {
          ja: "Amazonや楽天などのECサイトにブラウザ自動化でアクセス。ログイン、商品検索、カート追加、注文確定まで全自動。1Passwordと連携すればMFA認証も自動。",
          en: "Browser automation handles Amazon, Rakuten, etc. — login, search, add to cart, and checkout. 1Password integration handles MFA too.",
          zh: "浏览器自动化处理Amazon、乐天等网站的登录、搜索、加购和结算。",
        },
      },
      {
        title: {
          ja: "定期注文も自動化できる",
          en: "Automate recurring orders",
          zh: "定期订单也能自动化",
        },
        description: {
          ja: "「毎月1日にオフィス備品を注文して」と設定すれば、毎月自動で注文。注文完了したらSlackで「注文したよ！」と報告。買い忘れがゼロに。",
          en: "'Order office supplies on the 1st of every month.' Sophy confirms via Slack when done. Never forget a purchase again.",
          zh: "'每月1号订购办公用品'。完成后Sophy通过Slack报告。",
        },
      },
    ],
    tools: ["1Password", "Slack"],
  },
  {
    slug: "form-automation",
    icon: FileText,
    title: {
      ja: "面倒なフォーム入力・申請を自動化する",
      en: "Automate Tedious Form Filling & Applications",
      zh: "自动化繁琐的表单填写和申请",
    },
    description: {
      ja: "経費精算、勤怠入力、各種申請フォーム。毎回同じような入力をSophyが代行してくれる。",
      en: "Expense reports, attendance, various forms — Sophy fills them out for you automatically.",
      zh: "报销、考勤、各种申请表单，Sophy自动代为填写。",
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "「経費精算やっておいて」と頼む",
          en: "'Handle my expense report'",
          zh: "'帮我处理报销'",
        },
        description: {
          ja: "Sophyに領収書の内容を伝えるだけ。「3月15日、タクシー、3,200円、渋谷→六本木、会議移動」——Sophyが社内の経費精算フォームにブラウザで自動入力。",
          en: "Just share receipt details with Sophy. It fills in your company's expense form via browser automation.",
          zh: "只需告诉Sophy收据内容，它会通过浏览器自动填写公司的报销表单。",
        },
      },
      {
        title: {
          ja: "Sophyがフォームを操作する",
          en: "Sophy fills the form",
          zh: "Sophy操作表单",
        },
        description: {
          ja: "ブラウザ自動化で社内システムにアクセスし、必要なフィールドに正確に入力。日付、金額、科目、備考——人間がポチポチやっていた作業を全自動で。勤怠入力、出張申請、備品発注など、フォーム系はなんでも対応。",
          en: "Browser automation accesses internal systems and fills every field accurately. Attendance, travel requests, supply orders — any form works.",
          zh: "浏览器自动化访问内部系统，准确填写每个字段。考勤、出差申请、备品订购都能处理。",
        },
      },
      {
        title: {
          ja: "入力内容を確認して完了",
          en: "Review and submit",
          zh: "确认内容后提交",
        },
        description: {
          ja: "送信前に「この内容で申請するよ」と確認してくれるので安心。確認OKなら送信。「毎日18時に勤怠を自動入力」の定期設定も可能。月末の経費精算がまとめて数分で終わる。",
          en: "Sophy confirms before submitting: 'Submit this?' Schedule daily attendance entries or batch month-end expenses in minutes.",
          zh: "提交前Sophy会确认内容。也可以设置定期自动填写。",
        },
      },
    ],
    tools: ["Sophy"],
  },
  {
    slug: "web-scraping-monitoring",
    icon: Globe,
    title: {
      ja: "競合の価格・在庫を自動監視する",
      en: "Auto-Monitor Competitor Prices & Inventory",
      zh: "自动监控竞品价格和库存",
    },
    description: {
      ja: "ECサイトの価格変動、競合の新商品、在庫状況をAIが定期的にチェックしてレポート。",
      en: "AI regularly checks e-commerce price changes, competitor products, and stock levels.",
      zh: "AI定期检查电商价格变动、竞品新品和库存情况。",
    },
    category: "automation",
    steps: [
      {
        title: {
          ja: "監視したいページを伝える",
          en: "Tell Sophy what to monitor",
          zh: "告知要监控的页面",
        },
        description: {
          ja: "「このURLの商品価格を毎日チェックして」「競合A社の新着ページを毎週見て」とSophyに伝えるだけ。",
          en: "'Check this product URL daily' or 'Review competitor A's new arrivals weekly' — just tell Sophy.",
          zh: "'每天检查这个URL的商品价格'或'每周查看竞品A的新品页面'。",
        },
      },
      {
        title: {
          ja: "Sophyが定期的にチェック",
          en: "Sophy checks on schedule",
          zh: "Sophy定期检查",
        },
        description: {
          ja: "ブラウザ自動化でWebページにアクセスし、価格、在庫、新着情報をスクレイピング。変化があったときだけ通知する設定も可能。",
          en: "Browser automation scrapes prices, stock, and new items from web pages. Optionally notify only on changes.",
          zh: "浏览器自动化抓取价格、库存和新品信息。可设置仅在变化时通知。",
        },
      },
      {
        title: {
          ja: "変化を検知して即通知",
          en: "Instant alerts on changes",
          zh: "检测到变化立即通知",
        },
        description: {
          ja: "「A社が10%値下げしました」「在庫が残り3個になりました」「新商品が追加されました」——変化を検知したらSlackやLINEに即通知。EC担当やマーチャンダイザーの強い味方。",
          en: "'Company A dropped prices 10%,' 'Only 3 left in stock,' 'New product added' — instant Slack or LINE alerts on any change.",
          zh: "'A公司降价10%''库存仅剩3个''有新品上架'——检测到变化立即通过Slack或LINE通知。",
        },
      },
    ],
    tools: ["Slack", "LINE"],
  },
  {
    slug: "ai-banner-generation",
    icon: Megaphone,
    title: {
      ja: "SNS用バナーやサムネイルをAIで量産する",
      en: "Mass-Produce SNS Banners & Thumbnails with AI",
      zh: "用AI批量制作SNS横幅和缩略图",
    },
    description: {
      ja: "ブログのアイキャッチ、SNS広告のバナー、YouTubeのサムネイル。AIに作らせて、人間はクリエイティブの判断に集中。",
      en: "Blog headers, ad banners, YouTube thumbnails — let AI generate them while you focus on creative decisions.",
      zh: "博客封面、SNS广告横幅、YouTube缩略图，让AI生成，人专注创意判断。",
    },
    category: "creative",
    steps: [
      {
        title: {
          ja: "デザインの方向性を伝える",
          en: "Share your design direction",
          zh: "告知设计方向",
        },
        description: {
          ja: "「うちのブランドカラーは青系」「商品写真をメインに」「テキストはこのコピーで」——Sophyにイメージを伝えるだけ。過去に作ったバナーの参考画像を共有すると、さらに精度が上がる。",
          en: "Tell Sophy your brand colors, main visuals, and copy. Sharing reference images improves accuracy further.",
          zh: "告诉Sophy品牌色、主视觉和文案。分享参考图片可以进一步提高精度。",
        },
      },
      {
        title: {
          ja: "Sophyが複数パターンを生成",
          en: "Sophy generates multiple variations",
          zh: "Sophy生成多个方案",
        },
        description: {
          ja: "AI画像生成を使って、指定した条件で複数のバナーバリエーションを自動生成。サイズ違い（Instagram用、Twitter用、ブログ用）も一括で。1枚あたり数秒で生成されるので、10パターン作っても数分。",
          en: "AI generates multiple banner variations in different sizes (Instagram, Twitter, blog) in seconds. 10 patterns in just minutes.",
          zh: "AI按指定条件自动生成多个横幅变体，不同尺寸一次搞定。",
        },
      },
      {
        title: {
          ja: "選ぶだけで運用開始",
          en: "Pick and publish",
          zh: "选好就能开始使用",
        },
        description: {
          ja: "生成されたバナーはGoogle Driveに自動保存。気に入ったものを選んでSNSに投稿するだけ。A/Bテスト用のバリエーション作成も楽。デザイナーに依頼していた1枚5,000円のバナーが、AIなら数十円で。",
          en: "Generated banners auto-save to Google Drive. Just pick and post. Easy A/B testing too — what used to cost $50 per banner now costs pennies.",
          zh: "生成的横幅自动保存到Google Drive。选好发布即可。A/B测试变体也很轻松。",
        },
      },
    ],
    tools: ["Google Drive", "Seedance"],
  },
  {
    slug: "auto-presentation",
    icon: FileText,
    title: {
      ja: "プレゼン資料をAIに作ってもらう",
      en: "Let AI Create Your Presentations",
      zh: "让AI制作演示文稿",
    },
    description: {
      ja: "週次報告、プロジェクト提案、営業資料。Sophyに内容を伝えるだけで、構成から文面まで作成してくれる。",
      en: "Weekly reports, proposals, sales decks — just tell Sophy the content and it builds the whole presentation.",
      zh: "周报、项目提案、销售资料，只需告诉Sophy内容，从结构到文案全部搞定。",
    },
    category: "productivity",
    steps: [
      {
        title: {
          ja: "テーマと内容を伝える",
          en: "Share your topic and content",
          zh: "告知主题和内容",
        },
        description: {
          ja: "「来週の週次報告を作って。今週やったのはA機能のリリースとB案件の提案」——伝えたい内容をざっくり話すだけ。Sophyが構成（目次、各スライドの内容）を提案してくれる。",
          en: "'Create next week's report. This week we released Feature A and proposed Project B.' Sophy suggests the structure and slide contents.",
          zh: "'做下周的周报。这周完成了A功能发布和B项目提案。'Sophy会建议结构和各页内容。",
        },
      },
      {
        title: {
          ja: "Sophyが資料を作成する",
          en: "Sophy builds the deck",
          zh: "Sophy制作资料",
        },
        description: {
          ja: "提案した構成をもとに、各スライドのテキスト、箇条書き、データの見せ方を作成。Linearの完了タスクやSlackの議論を参照して、具体的な成果データも自動で組み込む。",
          en: "Based on the proposed structure, Sophy writes slide text, bullet points, and data presentations. It pulls completed tasks from Linear and discussions from Slack automatically.",
          zh: "根据提议的结构，Sophy撰写幻灯片文本，自动从Linear和Slack提取数据。",
        },
      },
      {
        title: {
          ja: "Notionに保存してすぐ使える",
          en: "Saved to Notion, ready to use",
          zh: "保存到Notion，随时可用",
        },
        description: {
          ja: "作成した資料はNotionやGoogle Driveに保存。「毎週金曜に来週の週次報告の下書きを作っておいて」と設定すれば、月曜の朝には報告資料の下書きが準備済み。報告書作成に1時間かけていたのが10分に。",
          en: "Saved to Notion or Google Drive. Schedule 'draft next week's report every Friday' and it's ready Monday morning. What took an hour now takes 10 minutes.",
          zh: "保存到Notion或Google Drive。设置'每周五准备下周周报草稿'，周一早上就能用。",
        },
      },
    ],
    tools: ["Notion", "Google Drive", "Linear", "Slack"],
  },
  {
    slug: "customer-support-automation",
    icon: MessageSquare,
    title: {
      ja: "顧客からの問い合わせにAIが一次対応する",
      en: "AI First-Response for Customer Inquiries",
      zh: "AI自动处理客户咨询的初次回复",
    },
    description: {
      ja: "メールやSlackで届く顧客からの質問に、Sophyが一次回答を作成。対応スピードが劇的に上がる。",
      en: "Sophy drafts first responses to customer questions from email or Slack, dramatically speeding up support.",
      zh: "Sophy为通过邮件或Slack收到的客户问题起草初次回复，大幅提升响应速度。",
    },
    category: "sales",
    steps: [
      {
        title: {
          ja: "よくある質問をSophyに覚えさせる",
          en: "Teach Sophy your FAQs",
          zh: "让Sophy记住常见问题",
        },
        description: {
          ja: "FAQ、製品マニュアル、過去の対応履歴をNotionやGoogle Driveに保存しておくだけ。Sophyがナレッジとして参照できるようになる。",
          en: "Save FAQs, product manuals, and past responses in Notion or Google Drive. Sophy uses them as its knowledge base.",
          zh: "将FAQ、产品手册和历史回复保存在Notion或Google Drive中，Sophy会作为知识库参考。",
        },
      },
      {
        title: {
          ja: "問い合わせが来たらSophyが下書き作成",
          en: "Sophy drafts replies automatically",
          zh: "收到咨询后Sophy自动起草回复",
        },
        description: {
          ja: "顧客からの問い合わせメールをSophyが読み、ナレッジベースから最適な回答を見つけて返信の下書きを作成。「このメールの返信書いて」と頼むだけでOK。",
          en: "Sophy reads customer emails, finds the best answer from its knowledge base, and drafts a reply. Just say 'write a reply to this email.'",
          zh: "Sophy阅读客户邮件，从知识库找到最佳答案并起草回复。只需说'帮我回复这封邮件'。",
        },
      },
      {
        title: {
          ja: "確認して送信するだけ",
          en: "Review and send",
          zh: "确认后发送",
        },
        description: {
          ja: "Sophyが作った下書きを確認し、必要があれば修正して送信。対応に30分かかっていた問い合わせが5分に。「返信の下書きができたよ」とSlackで通知してくれるので、見逃しもゼロ。",
          en: "Review Sophy's draft, edit if needed, and send. What took 30 minutes now takes 5. Sophy notifies you on Slack when drafts are ready — zero missed inquiries.",
          zh: "确认Sophy的草稿，必要时修改后发送。30分钟的工作缩短到5分钟。Sophy会通过Slack通知草稿完成。",
        },
      },
    ],
    tools: ["Gmail", "メール", "Notion", "Slack"],
  },
]

// ツール定義は @/(marketing)/_data/tools.ts に一元管理
export { allTools, toolByName } from "@/app/(marketing)/_data/tools"
